// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Agents data the app holds while it is on screen (docs/konvo-agents/DESIGN.md).
// Konvo keeps no thread content: everything here is read live from the machine through the API
// and dropped on sign-out. The overview (machines, online state, limits) comes from GET
// /konvo/agents; thread lists and thread details are live reads; KONVO_AGENTS_UPDATE events keep
// them fresh. Writes the person makes here (send, start, stop, Allow/Deny) run straight away.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {
	type AgentAccount,
	machineAccounts,
	type OtherLogin,
	otherLoginWithRoom,
	planAccentColor,
	planAccounts,
	planIsOut,
} from '@app/features/konvo_agents/state/AgentAccounts';
import AgentAsks from '@app/features/konvo_agents/state/AgentAsks';
import {saveDraftFiles} from '@app/features/konvo_agents/state/AgentDraftFiles';
import AgentDrafts, {NEW_CHAT_DRAFT, threadDraftKey} from '@app/features/konvo_agents/state/AgentDrafts';
import {deleteOutgoing, loadOutgoing, saveOutgoing} from '@app/features/konvo_agents/state/AgentOutbox';
import AgentPullInbox from '@app/features/konvo_agents/state/AgentPullInbox';
import AgentPulls from '@app/features/konvo_agents/state/AgentPulls';
import AgentRuns from '@app/features/konvo_agents/state/AgentRuns';
import AgentShares from '@app/features/konvo_agents/state/AgentShares';
import AgentsAccess from '@app/features/konvo_agents/state/AgentsAccess';
import {
	type AgentChatContext,
	agentErrorCode,
	cameOnline,
	countThreads,
	finishedAt,
	isReleaseGap,
	isSettled,
	isUnseen,
	PENDING_THREAD_PREFIX,
	seenKey,
	shouldReplaceDetail,
	snoozeEnded,
	sortThreads,
	summaryDiffers,
	withoutQuestions,
} from '@app/features/konvo_agents/state/AgentsModel';
import {
	type AgentToolProvider,
	type AgentToolUpdate,
	type AgentUpdateAsk,
	type AgentUpdateEnd,
	agentToolUpdates,
	agentUpdateKey,
	endedAgentUpdates,
} from '@app/features/konvo_agents/state/AgentToolUpdates';
import {uploadAgentFiles} from '@app/features/konvo_agents/state/AgentUploads';
import {agentWebOrigin} from '@app/features/konvo_agents/state/AgentWebOrigin';
import {type AgentMentionInput, mentionWire} from '@app/features/konvo_agents/utils/AgentMentions';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import type {RestResponse} from '@app/features/platform/types/TransportTypes';
import {awaitHydration} from '@app/features/platform/utils/MobXPersistence';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import Users from '@app/features/user/state/Users';
import type {
	AgentAccountRequest,
	AgentAnswers,
	AgentBranches,
	AgentCloudHandoffPreview,
	AgentCommands,
	AgentCommandsRequest,
	AgentContinuedIn,
	AgentDecision,
	AgentDiff,
	AgentMachineView,
	AgentModelChoice,
	AgentPairing,
	AgentProject,
	AgentProjectIcon,
	AgentPullRequest,
	AgentQuestionUploads,
	AgentQueuedSend,
	AgentSearch,
	AgentSecretAnswer,
	AgentSetupRequest,
	AgentSnooze,
	AgentsOverview,
	AgentsPins,
	AgentsPrefs,
	AgentsProjectIcons,
	AgentsSeen,
	AgentsSnoozes,
	AgentsUpdateEvent,
	AgentThreadDetail,
	AgentThreadItem,
	AgentThreadSource,
	AgentThreadSummary,
	AgentToolInfo,
	AgentUploadInput,
	AgentUsageResetResult,
} from '@fluxer/schema/src/domains/konvo/Agents';
import {
	type AgentPlanUsageView,
	type AgentUsageWindowView,
	agentFamily,
	agentThreadPlan,
	mergeAgentPlanUsage,
} from '@fluxer/schema/src/domains/konvo/AgentUsage';
import {i18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {makeAutoObservable, observable, reaction, runInAction} from 'mobx';

const UPDATE_OFFLINE_DESCRIPTOR = msg({
	message: '{machine} is offline.',
	comment:
		'Why updating Claude Code or Codex from the Agents page failed. {machine} is the machine name, e.g. "interns".',
});
const UPDATE_READ_ONLY_DESCRIPTOR = msg({
	message: '{machine} only shares its chats for reading.',
	comment:
		'Why updating Claude Code or Codex from the Agents page failed: the machine owner allowed reading only. {machine} is the machine name.',
});
const UPDATE_BRIDGE_OLD_DESCRIPTOR = msg({
	message: 'Update konvo-agents on {machine} first.',
	comment:
		'Why updating Claude Code or Codex from the Agents page failed: the Konvo bridge on the machine is too old. "konvo-agents" is the program name; do not translate it. {machine} is the machine name.',
});
const UPDATE_TIMEOUT_DESCRIPTOR = msg({
	message: '{machine} did not answer in time.',
	comment: 'Why updating Claude Code or Codex from the Agents page failed. {machine} is the machine name.',
});
const UPDATE_FAILED_DESCRIPTOR = msg({
	message: 'Konvo could not start the update.',
	comment: 'Why updating Claude Code or Codex from the Agents page failed, when nothing more specific is known.',
});

const BACK_ONLINE_DESCRIPTOR = msg({
	message: '{name} is back online',
	comment: 'Toast when a machine the person asked about ("Tell me when it\'s back") connects to Konvo again.',
});

export interface MachineThreads {
	threads: Array<AgentThreadSummary> | null;
	projects: Array<AgentProject>;
	loading: boolean;
	error: unknown;
	loadedAt: number;
}

/** A machine's archived threads (konvo-agents 0.36.0+), read when the list shows. */
export interface ArchivedThreads {
	threads: Array<AgentThreadSummary> | null;
	loading: boolean;
	error: unknown;
}

export interface ThreadState {
	detail: AgentThreadDetail | null;
	loading: boolean;
	error: unknown;
	/** When data for this thread last arrived (a fetch or a live update). */
	receivedAt: number;
	/** Older items loaded on scrolling to the top (live reads only return the latest turns). */
	earlier?: Array<AgentThreadItem>;
	earlierCursor?: string | null;
	loadingEarlier?: boolean;
	/** The last earlier page failed: wait for the person to try again rather than retrying on every scroll. */
	earlierFailed?: boolean;
}

export interface StartThreadArgs {
	sendId?: string;
	projectId: string;
	prompt: string;
	title?: string;
	worktree?: boolean;
	branch?: string;
	/** Puts the checkout's changes aside before switching it to branch (konvo-agents 0.41.0+, can.checkoutHelp). */
	stash?: boolean;
	/** Runs the chat in Claude's cloud, from branch as GitHub has it (konvo-agents 0.54.0+, can.cloud). */
	cloud?: boolean;
	planMode?: boolean;
	/** Left out: the project's model (konvo-agents 0.9.0+ takes one). */
	model?: AgentModelChoice;
	/** Pictures and files with the first prompt (needs can.startFiles). */
	attachments?: Array<AgentUploadInput>;
	/** The message or email it was sent from; Konvo keeps it for Reply to … */
	source?: AgentThreadSource;
}

/**
 * Silent reads resolve on 4xx/5xx with the error body rather than throwing, a release's 502s
 * too. Read as data, a failed overview emptied the page into "Connect a machine": fail instead.
 */
function answered<R extends RestResponse<unknown>>(response: R): R {
	if (!response.ok) throw new HttpError({method: 'GET', path: '', status: response.status, body: response.body});
	return response;
}

const threadKey = (machineId: string, threadId: string) => `${machineId}\u0000${threadId}`;

/** A thread's list summary from its detail. */
function summaryOf(detail: AgentThreadDetail): AgentThreadSummary {
	const {
		items: _items,
		approvals: _approvals,
		questions: _questions,
		revision: _revision,
		page: _page,
		diffTurn: _diffTurn,
		...summary
	} = detail;
	return summary;
}

function withoutArchivedAt<T extends AgentThreadSummary>(thread: T): T {
	const {archivedAt: _archivedAt, ...rest} = thread;
	return rest as T;
}

/** How long a settle the machine has taken still wins over lists and threads that say otherwise. */
const SETTLE_GRACE_MS = 10_000;
/** How long a dismissed question stays hidden after the machine took it, against reads already on their way. */
const DISMISS_GRACE_MS = 5_000;
/** How long a started chat's page waits for the thread's first read before it swaps (it swaps anyway). */
const START_PRELOAD_MS = 4_000;
/** How long a started chat's entry is kept for a page that may still swap to its thread. */
const START_FORGET_MS = 30_000;
/** A prompt sent while Konvo is mid-release keeps trying this long before it shows as not sent. */
const RELEASE_GAP_PATIENCE_MS = 2 * 60_000;
/** How often it looks again in the meantime. */
const RELEASE_GAP_RETRY_MS = 3_000;

/** A prompt on its way to a thread: on screen at once, until the thread shows it or it fails. */
export interface AgentOutgoing {
	editing?: true;
	id: string;
	machineId: string;
	threadId: string;
	prompt: string;
	files: ReadonlyArray<File>;
	/** Switches the thread to this model first. */
	model: AgentModelChoice | null;
	state: 'sending' | 'sent' | 'failed';
	phase?: 'saving' | 'uploading' | 'linking' | 'accepted' | 'failed' | 'uncertain';
	holdUntil?: string;
	startId?: string;
	steer?: boolean;
	uploads?: ReadonlyArray<AgentUploadInput>;
	reason?: 'limit';
	error: unknown;
	/** When it last failed (ms), for the failure card's details. */
	failedAt?: number;
	/** The thread's prompts when this one left: its copy is the first other one with the same text. */
	before: ReadonlyArray<string>;
	/** Waits for the running turn to end (T3's queue) instead of steering into it. */
	queue?: boolean;
}

/**
 * A new chat on its way to the machine: its page shows at once (AgentPendingThread) and its row
 * sits at the top of its project until the thread exists, or it shows as not started.
 */
export interface AgentPendingStart {
	editing?: true;
	id: string;
	machineId: string;
	/** What the machine gets, but its pictures and files (sent from `files`). */
	args: Omit<StartThreadArgs, 'attachments'>;
	files: ReadonlyArray<File>;
	/** What the person typed and the item it started from: Edit puts them back in the new chat. */
	typed: string;
	context: AgentChatContext | null;
	/** For the page and the row: where it works and on what. */
	branch: string | null;
	modelName: string | null;
	provider: string | null;
	startedAt: string;
	/** 'waiting': another chat was asked to commit the checkout's changes; it starts when that one is done. */
	state: 'starting' | 'started' | 'failed' | 'waiting';
	error: unknown;
	/** Set once the machine started it. */
	threadId: string | null;
	/** The chat asked to commit (state 'waiting'). */
	waitingOn?: {threadId: string; title: string};
	/** When the thread could be read (state 'started'); its page swaps to the thread then. */
	doneAt?: number;
}

/**
 * How a chat that didn't start is tried again: in a new worktree from its branch, in the checkout as
 * it is (no switch), or with the checkout's changes put aside first. `shownBranch` is what its page
 * and row show (null: "New worktree").
 */
export interface StartChange {
	worktree?: boolean;
	/** null: no switch, start on the checkout's own branch. */
	branch?: string | null;
	stash?: boolean;
	shownBranch: string | null;
}

/** How long a chat waits for the one asked to commit before it gives up (and shows as not started). */
const COMMIT_WAIT_MS = 2 * 60 * 60 * 1000;
const COMMIT_POLL_MS = 15_000;
/** Each waiting chat's stop (watch, poll, give-up timer), kept off the store. */
const commitWaits = new Map<string, () => void>();

/** What the asked chat gets: commit what is in the checkout, so another chat can switch it. */
export function commitAskPrompt(current: string | null, branch: string): string {
	const on = current ? ` on ${current}` : '';
	return `Please commit the uncommitted changes in this checkout${on} (stage them and commit with a fitting message; leave out anything that should not be committed and say what you left). Another Konvo chat is waiting to switch this checkout to ${branch} and start there once you're done.`;
}

/** A new chat's title until the agent tool names it: its first line, as T3 does. */
export function pendingTitle(prompt: string): string {
	const line = prompt.trim().split('\n')[0]?.trim() ?? '';
	return line.length > 100 ? `${line.slice(0, 99)}…` : line;
}

export {agentFamily};

/** Why a thread stopped on its plan, and when it can go on (when known). */
export interface ThreadLimit {
	kind: 'usage' | 'credits';
	resetsAt: string | null;
	/** The reset time has passed: the person can send again. */
	over: boolean;
	providerName: string | null;
}

/** "machine/thread" for the chat open in the Agents page, or null when none is. */
function followKey(machineId: string | null, threadId: string | null): string | null {
	return machineId ? `${machineId}/${threadId ?? ''}` : null;
}

export class AgentsStore {
	/** From the last overview; null before the first answer. */
	enabled: boolean | null = null;
	machines: Array<AgentMachineView> = [];
	loaded = false;
	loading = false;
	error: unknown = null;
	/** While Konvo does not answer: when the overview is asked for again (the "Trying again in 4s" pill). */
	retryAt: number | null = null;
	readonly setupOpen = observable.set<string>();
	readonly lists = observable.map<string, MachineThreads>();
	readonly threads = observable.map<string, ThreadState>();
	prefs: AgentsPrefs | null = null;
	prefsError: unknown = null;
	/** Which finished threads the person has opened (account-wide); null until loaded. */
	seen: AgentsSeen | null = null;
	/** Threads the person pinned (account-wide), `<machineId>/<threadId>`, the latest pin first. */
	pins: Array<string> = [];
	/** Chats the person snoozed (account-wide), the latest first; ended ones until opened. */
	snoozes: Array<AgentSnooze> = [];
	/** Icons the person set on projects (account-wide), by `<machineId>/<projectId>`. */
	projectIcons: Record<string, AgentProjectIcon> = {};
	/** Machines the person asked to hear about when they come back ("Tell me when it's back"). */
	readonly waitingFor = observable.set<string>();
	/** Settings → Connections should open straight on the machines manager. */
	managerRequested = false;
	/** The online machine the list shows (picked from the machine menu); null = the first online one. */
	listMachineId: string | null = null;
	/** The chat the list last followed ("machine/thread"): opening a chat shows its machine, a pick after that wins. */
	listFollowed: string | null = null;
	/** The machine whose settled chats replace the live list (the Settled chip), if any. */
	settledMachineId: string | null = null;
	/** The machine whose archived chats replace the live list (the Archived view), if any. */
	archivedMachineId: string | null = null;
	/** Archived threads by machine. */
	readonly archived = observable.map<string, ArchivedThreads>();
	/** A message a search found: the thread opens scrolled to it, then this clears. */
	focusMessage: {machineId: string; threadId: string; at: string} | null = null;
	/** The project chip picked while Settled is on; null = every project. */
	settledProject: string | null = null;
	/** The list row whose title is being renamed in place, if any. */
	listRename: {machineId: string; threadId: string} | null = null;
	/** What "Send to an agent" brought to the new chat page, and where its project was guessed from. */
	newChatContext: AgentChatContext | null = null;
	/** The model the next new chat starts on, set by "Continue on … now", until it starts or another is picked. */
	newChatModel: AgentModelChoice | null = null;
	newChatGuess: string | null = null;
	/** True while the Agents page is open (it loads thread lists for machines that come online). */
	listsWanted = false;
	/** Prompts Konvo holds for threads (e.g. "continue" once a limit resets); null until loaded. */
	queued: Array<AgentQueuedSend> | null = null;
	/** Ticks each minute so usage windows whose reset time has passed read as reset. */
	minute = Date.now();
	/** Claude Code and Codex updates asked for from this app, by machine and provider (AgentToolUpdates.ts). */
	readonly updateAsks = observable.map<string, AgentUpdateAsk>();
	/** When this app saw each of those updates end (AgentToolUpdates.ts). */
	readonly updateEnds = observable.map<string, AgentUpdateEnd>();
	/** Prompts sent from here that their threads don't show yet, oldest first. */
	readonly outbox = observable.array<AgentOutgoing>([], {deep: false});
	/** New chats started from here whose threads the list doesn't show yet, oldest first. */
	private readonly editingSends = new Map<string, Set<string>>();
	readonly queueActions = observable.set<string>();
	readonly pendingStarts = observable.array<AgentPendingStart>([], {deep: false});
	private overviewRequest: Promise<void> | null = null;
	/** Settles the person made, by thread: they win over older reads until the grace time is up. */
	private readonly settling = new Map<string, {settled: boolean; until: number}>();
	/** Titles the person gave threads, by thread: they win over older reads the same way. */
	private readonly renaming = new Map<string, {title: string; until: number}>();
	/**
	 * Threads the person archived, unarchived or deleted, by thread: an archived or deleted one stays
	 * out of the live list, an unarchived one in it, over reads from before the machine caught up.
	 */
	private readonly archiving = new Map<string, {live: AgentThreadSummary | null; until: number}>();
	/** Questions the person dismissed, per thread: hidden while it is on its way and for a moment after. */
	private readonly dismissing = new Map<string, {ids: Set<string>; until: number}>();
	/** Each thread's prompts go one after another, in the order they were sent. */
	private readonly sendChains = new Map<string, Promise<void>>();

	constructor() {
		makeAutoObservable<this, 'overviewRequest' | 'settling' | 'renaming' | 'archiving' | 'dismissing' | 'sendChains'>(
			this,
			{
				overviewRequest: false,
				settling: false,
				renaming: false,
				archiving: false,
				dismissing: false,
				sendChains: false,
				// Kept as given: an email's whole body rides along, and nothing edits it in place.
				newChatContext: observable.ref,
			},
			{autoBind: true},
		);
		if (typeof window !== 'undefined')
			window.setInterval(
				() =>
					runInAction(() => {
						this.minute = Date.now();
					}),
				60_000,
			);
		reaction(
			() => Users.currentUserId,
			(next, previous) => {
				if (next !== previous) {
					this.reset();
					if (next) void this.restoreOutgoing(next);
				}
			},
			{fireImmediately: true},
		);
	}

	private async restoreOutgoing(owner: string): Promise<void> {
		try {
			const saved = await loadOutgoing(owner);
			await awaitHydration('AgentDrafts');
			if (Users.currentUserId !== owner) return;
			runInAction(() => {
				for (const record of saved) {
					if (record.value.editing) {
						const key =
							record.kind === 'message'
								? threadDraftKey(record.value.machineId, record.value.threadId)
								: NEW_CHAT_DRAFT;
						this.rememberEditing(key, record.value.id);
						if (!AgentDrafts.get(key)) {
							AgentDrafts.set(key, record.kind === 'message' ? record.value.prompt : record.value.typed);
							AgentDrafts.setFileCount(key, record.value.files.length);
							void saveDraftFiles(key, record.value.files);
							if (record.kind === 'start') this.setNewChatContext(record.value.context);
						}
						continue;
					}
					if (record.kind === 'message' && !this.outgoing(record.value.id)) {
						this.outbox.push({
							...record.value,
							state: record.value.state === 'sent' ? 'sent' : 'failed',
							phase:
								record.value.state === 'sent'
									? 'accepted'
									: record.value.state === 'sending'
										? 'uncertain'
										: (record.value.phase ?? 'failed'),
							error:
								record.value.state === 'failed'
									? record.value.error
									: new Error(i18n._(msg({message: 'Sending was interrupted. Your message has been kept.'}))),
						});
						if (record.value.holdUntil) void this.loadQueued();
						else void this.loadThread(record.value.machineId, record.value.threadId);
					} else if (record.kind === 'start' && !this.pendingStart(record.value.id)) {
						this.pendingStarts.push({
							...record.value,
							state: record.value.threadId ? 'started' : 'failed',
							error:
								record.value.state === 'failed'
									? record.value.error
									: new Error(i18n._(msg({message: 'Starting was interrupted. Your message has been kept.'}))),
						});
					}
				}
			});
		} catch {
			/* A blocked database leaves the existing composer drafts untouched. */
		}
	}

	private rememberEditing(key: string, id: string): void {
		const ids = this.editingSends.get(key) ?? new Set<string>();
		ids.add(id);
		this.editingSends.set(key, ids);
	}

	private consumeEdited(key: string): void {
		for (const id of this.editingSends.get(key) ?? [])
			void deleteOutgoing(Users.currentUserId!, id).catch(() => undefined);
		this.editingSends.delete(key);
	}

	// ── Reads ────────────────────────────────────────────────────────────

	machine(machineId: string | null | undefined): AgentMachineView | null {
		if (!machineId) return null;
		return this.machines.find((machine) => machine.id === machineId) ?? null;
	}

	list(machineId: string): MachineThreads | null {
		return this.lists.get(machineId) ?? null;
	}

	/** The machine's threads in list order, or null before they loaded. */
	threadsOf(machineId: string): Array<AgentThreadSummary> | null {
		const threads = this.lists.get(machineId)?.threads;
		return threads ? sortThreads(threads) : null;
	}

	/** Shared projects: the machine's list, plus any the last thread read returned. */
	projectsOf(machineId: string): Array<AgentProject> {
		const machine = this.machine(machineId);
		const fromList = this.lists.get(machineId)?.projects ?? [];
		const byId = new Map<string, AgentProject>();
		// The list's copy is newer, but only the machine's report carries the favicon (bridge 0.63.0+).
		for (const project of [...(machine?.projects ?? []), ...fromList]) {
			const known = byId.get(project.id);
			byId.set(project.id, known?.favicon && !project.favicon ? {...project, favicon: known.favicon} : project);
		}
		return [...byId.values()];
	}

	/** Running / needs-you counts: from the live list when loaded, else the overview's. */
	countsOf(machineId: string): {running: number; needsYou: number} {
		const machine = this.machine(machineId);
		if (!machine?.online) return {running: 0, needsYou: 0};
		const threads = this.lists.get(machineId)?.threads;
		return threads ? countThreads(threads) : machine.counts;
	}

	/** Threads that need the person across every online machine. */
	get needsYouTotal(): number {
		return this.machines.reduce((sum, machine) => sum + this.countsOf(machine.id).needsYou, 0);
	}

	/** Finished threads the person hasn't opened yet, across the loaded lists. */
	get unseenTotal(): number {
		let total = 0;
		for (const machine of this.machines) {
			if (!machine.online) continue;
			for (const thread of this.lists.get(machine.id)?.threads ?? []) {
				if (isUnseen(this.seen, machine.id, thread)) total += 1;
			}
		}
		return total;
	}

	/** The sidebar badge: threads that need the person plus finished ones not opened yet. */
	get badgeTotal(): number {
		// Pull requests someone asked this person to merge count as needing them.
		return this.needsYouTotal + this.unseenTotal + (AgentPulls.asks?.length ?? 0);
	}

	isUnseen(machineId: string, thread: AgentThreadSummary): boolean {
		return isUnseen(this.seen, machineId, thread);
	}

	thread(machineId: string, threadId: string): ThreadState | null {
		return this.threads.get(threadKey(machineId, threadId)) ?? null;
	}

	/** The thread as the live list (or, archived, the archived list) has it. */
	summary(machineId: string, threadId: string): AgentThreadSummary | null {
		return (
			this.lists.get(machineId)?.threads?.find((thread) => thread.id === threadId) ??
			this.archived.get(machineId)?.threads?.find((thread) => thread.id === threadId) ??
			null
		);
	}

	/** The machine's archived threads, newest archived first, or null before they loaded. */
	archivedOf(machineId: string): Array<AgentThreadSummary> | null {
		const threads = this.archived.get(machineId)?.threads;
		return threads ? [...threads].sort((a, b) => (b.archivedAt ?? '').localeCompare(a.archivedAt ?? '')) : null;
	}

	/** Archived, as far as Konvo knows: in the archived list, or opened and saying so. */
	isArchived(machineId: string, threadId: string): boolean {
		const key = threadKey(machineId, threadId);
		// Just unarchived: older reads still say archived.
		const pin = this.archiving.get(key);
		if (pin?.live && Date.now() <= pin.until) return false;
		return this.isListedArchived(machineId, threadId) || Boolean(this.threads.get(key)?.detail?.archivedAt);
	}

	private isListedArchived(machineId: string, threadId: string): boolean {
		return Boolean(this.archived.get(machineId)?.threads?.some((thread) => thread.id === threadId));
	}

	// ── Plan usage ───────────────────────────────────────────────────────

	/** Every plan the person's machines report (konvo-agents 0.10.0+), one per account. */
	get plans(): Array<AgentPlanUsageView> {
		return mergeAgentPlanUsage(this.machines, this.minute);
	}

	async commands(machineId: string, context: AgentCommandsRequest): Promise<AgentCommands> {
		return answered(
			await http.post<AgentCommands>(Endpoints.KONVO_AGENTS_COMMANDS(machineId), {body: context, timeoutMs: 65_000}),
		).body;
	}

	/** Pick an owner's online machine reporting this exact account and reset balance. */
	usageResetMachine(plan: AgentPlanUsageView): AgentMachineView | undefined {
		if (!plan.account || !plan.resetCredits?.availableCount) return undefined;
		return this.machines.find(
			(machine) =>
				machine.online &&
				machine.limits.send &&
				machine.tools.some(
					(tool) =>
						tool.can.resetUsage === true &&
						tool.usage?.some(
							(usage) =>
								usage.provider === plan.provider &&
								usage.account === plan.account &&
								usage.resetCredits?.availableCount === plan.resetCredits?.availableCount,
						),
				),
		);
	}

	async resetUsage(plan: AgentPlanUsageView): Promise<AgentUsageResetResult> {
		const machine = this.usageResetMachine(plan);
		if (!machine || !plan.account || !plan.resetCredits)
			throw new Error('No machine is available for this usage reset.');
		try {
			const response = answered(
				await http.post<AgentUsageResetResult>(Endpoints.KONVO_AGENTS_USAGE_RESET(machine.id), {
					body: {provider: plan.provider, account: plan.account, availableCount: plan.resetCredits.availableCount},
					timeoutMs: 65_000,
				}),
			);
			return response.body;
		} finally {
			await this.loadOverview();
		}
	}

	/** The fullest window across every plan: drives the Agents icon meter. */
	get fullestWindow(): {plan: AgentPlanUsageView; window: AgentUsageWindowView} | null {
		let best: {plan: AgentPlanUsageView; window: AgentUsageWindowView} | null = null;
		for (const plan of this.plans) {
			for (const window of plan.windows) {
				if (!best || window.usedPercent > best.window.usedPercent) best = {plan, window};
			}
		}
		return best;
	}

	/** Each plan's login mark (by plan key), when the person has two plans of one agent. */
	get planAccounts(): Map<string, AgentAccount> {
		return planAccounts(this.plans, this.machines);
	}

	planColor(plan: AgentPlanUsageView): string | undefined {
		return planAccentColor(plan, this.machines);
	}

	/** The logins on a machine (by provider instance), when it has two of one agent. */
	accountsOn(machineId: string): Map<string, AgentAccount> {
		return machineAccounts(this.machine(machineId));
	}

	/** The machine's logins (provider instances) whose plan is used up, when it has two of an agent. */
	usedUpLogins(machineId: string): Set<string> {
		const machine = this.machine(machineId);
		const out = new Set<string>();
		for (const instance of this.accountsOn(machineId).keys()) {
			const plan = agentThreadPlan(this.plans, machine, {provider: instance, instance});
			if (plan && planIsOut(plan)) out.add(instance);
		}
		return out;
	}

	/** The login a thread runs on, when its machine has two of its agent; null otherwise. */
	threadAccount(machineId: string, thread: AgentThreadSummary | null | undefined): AgentAccount | null {
		const instance = thread?.modelChoice?.provider;
		return instance ? (this.accountsOn(machineId).get(instance) ?? null) : null;
	}

	/** The plan a thread on this machine runs on: its login when the machine has two of one agent. */
	planOf(machineId: string, thread: AgentThreadSummary): AgentPlanUsageView | null {
		return agentThreadPlan(this.plans, this.machine(machineId), {
			provider: thread.provider,
			instance: thread.modelChoice?.provider,
		});
	}

	/** A thread that stopped on its plan: why, and when the plan's used-up window resets. */
	limitOf(machineId: string, thread: AgentThreadSummary | null | undefined): ThreadLimit | null {
		if (!thread?.limit || thread.status !== 'error') return null;
		const plan = this.planOf(machineId, thread);
		let resetsAt = thread.limit.resetsAt ?? null;
		if (!resetsAt && plan) {
			const out = plan.windows.filter((window) => window.usedPercent >= 100 && window.resetsAt);
			resetsAt =
				out
					.map((window) => window.resetsAt!)
					.sort()
					.at(-1) ?? null;
		}
		const passed = resetsAt != null && Date.parse(resetsAt) <= this.minute;
		const reset = plan?.windows.some((window) => window.reset) ?? false;
		return {
			kind: thread.limit.kind,
			resetsAt,
			over: thread.limit.kind === 'usage' && (passed || (!resetsAt && reset)),
			providerName: plan?.providerName ?? null,
		};
	}

	queuedFor(machineId: string, threadId: string): AgentQueuedSend | null {
		return this.queued?.find((entry) => entry.machineId === machineId && entry.threadId === threadId) ?? null;
	}

	async loadQueued(): Promise<void> {
		try {
			const response = answered(
				await http.get<{queued: Array<AgentQueuedSend>}>(Endpoints.KONVO_AGENTS_QUEUED, {
					mode: 'silent',
				}),
			);
			runInAction(() => {
				this.queued = response.body.queued ?? [];
				for (const entry of [...this.outbox]) {
					if (
						!entry.holdUntil ||
						!this.queued.some(
							(queued) =>
								queued.sendId === entry.id &&
								queued.machineId === entry.machineId &&
								queued.threadId === entry.threadId,
						)
					)
						continue;
					this.outbox.remove(entry);
					void deleteOutgoing(Users.currentUserId!, entry.id).catch(() => undefined);
				}
			});
		} catch {
			// The thread still works without it; the waiting prompt just isn't shown.
		}
	}

	/** Konvo sends the prompt at `at` (replacing one already waiting for the thread). */
	async queueSend(
		machineId: string,
		threadId: string,
		prompt: string,
		at: string,
		reason?: 'limit',
		files: ReadonlyArray<File> = [],
		model: AgentModelChoice | null = null,
	): Promise<void> {
		await this.post(machineId, threadId, prompt, files, model, false, at, reason);
	}

	async retryHeld(machineId: string, threadId: string): Promise<void> {
		const entry = this.queuedFor(machineId, threadId);
		if (!entry || entry.state !== 'failed') return;
		const response = await http.post<AgentQueuedSend>(Endpoints.KONVO_AGENTS_THREAD_QUEUED(machineId, threadId), {
			body: {
				...entry,
				at: new Date().toISOString(),
				mentions: entry.mentions?.map((mention) =>
					mention.kind === 'chat' ? {...mention, machineId: mention.machineId ?? machineId} : mention,
				),
			},
		});
		runInAction(() => {
			this.queued = (this.queued ?? []).map((queued) => (queued === entry ? response.body : queued));
		});
	}

	async cancelQueued(machineId: string, threadId: string): Promise<void> {
		const before = this.queued;
		this.queued = (before ?? []).filter((entry) => entry.machineId !== machineId || entry.threadId !== threadId);
		try {
			await http.delete(Endpoints.KONVO_AGENTS_THREAD_QUEUED(machineId, threadId));
		} catch (error) {
			runInAction(() => {
				this.queued = before;
			});
			throw error;
		}
	}

	get onlineCount(): number {
		return this.machines.filter((machine) => machine.online).length;
	}

	// ── Overview ─────────────────────────────────────────────────────────

	loadOverview(): Promise<void> {
		if (this.overviewRequest) return this.overviewRequest;
		const request = this.fetchOverview().finally(() => {
			this.overviewRequest = null;
		});
		this.overviewRequest = request;
		return request;
	}

	private async fetchOverview(): Promise<void> {
		this.loading = true;
		try {
			const response = answered(await http.get<AgentsOverview>(Endpoints.KONVO_AGENTS, {mode: 'silent'}));
			const body = response.body;
			runInAction(() => {
				const before = this.machines;
				this.enabled = body.enabled;
				this.machines = body.enabled ? body.machines : [];
				this.noteUpdateEnds(before, this.machines);
				this.loaded = true;
				this.error = null;
				this.retryAt = null;
				for (const id of [...this.lists.keys()]) {
					const machine = this.machine(id);
					if (!machine) this.lists.delete(id);
					else if (!machine.online) {
						// Offline: Konvo keeps no copies, so its chats leave the screen too.
						this.lists.delete(id);
					}
				}
				const back = cameOnline(before, this.machines);
				for (const id of back) {
					if (this.waitingFor.has(id)) {
						this.waitingFor.delete(id);
						const name = this.machine(id)?.name ?? '';
						ToastCommands.success(i18n._(BACK_ONLINE_DESCRIPTOR, {name}));
					}
				}
			});
			AgentsAccess.note(body.enabled);
		} catch (error) {
			runInAction(() => {
				this.error = error;
				this.loaded = true;
			});
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}

	// ── Thread lists and threads ─────────────────────────────────────────

	async loadThreads(machineId: string): Promise<void> {
		const current = this.lists.get(machineId);
		this.lists.set(machineId, {
			threads: current?.threads ?? null,
			projects: current?.projects ?? [],
			loading: true,
			error: null,
			loadedAt: current?.loadedAt ?? 0,
		});
		try {
			const response = answered(
				await http.get<{threads: Array<AgentThreadSummary>; projects: Array<AgentProject>}>(
					Endpoints.KONVO_AGENTS_THREADS(machineId),
					{mode: 'silent'},
				),
			);
			runInAction(() => {
				this.lists.set(machineId, {
					threads: this.withArchiving(machineId, this.withSettling(machineId, response.body.threads ?? [])),
					projects: response.body.projects ?? [],
					loading: false,
					error: null,
					loadedAt: Date.now(),
				});
				this.forgetStarted(machineId);
			});
		} catch (error) {
			runInAction(() => {
				const latest = this.lists.get(machineId);
				this.lists.set(machineId, {
					threads: latest?.threads ?? null,
					projects: latest?.projects ?? [],
					loading: false,
					error,
					loadedAt: latest?.loadedAt ?? 0,
				});
			});
		}
	}

	/** Thread reads and live updates in order, so a read asked for earlier that lands later is not shown. */
	private threadReads = 0;
	private readonly appliedThreadRead = new Map<string, number>();

	async loadThread(machineId: string, threadId: string): Promise<void> {
		const key = threadKey(machineId, threadId);
		const read = ++this.threadReads;
		const current = this.threads.get(key);
		this.threads.set(key, {
			...current,
			detail: current?.detail ?? null,
			loading: true,
			error: null,
			receivedAt: current?.receivedAt ?? 0,
		});
		try {
			const response = answered(
				await http.get<AgentThreadDetail>(Endpoints.KONVO_AGENTS_THREAD(machineId, threadId), {
					mode: 'silent',
				}),
			);
			runInAction(() => {
				// A read asked for before the one on screen can miss a prompt it shows (the prompt
				// would vanish and the turn before it unfold for a moment).
				if (read < (this.appliedThreadRead.get(key) ?? 0)) {
					const latest = this.threads.get(key);
					if (latest) this.threads.set(key, {...latest, loading: false});
					return;
				}
				this.appliedThreadRead.set(key, read);
				this.putDetail(machineId, response.body);
			});
		} catch (error) {
			runInAction(() => {
				const latest = this.threads.get(key);
				this.threads.set(key, {
					...latest,
					detail: latest?.detail ?? null,
					loading: false,
					error,
					receivedAt: latest?.receivedAt ?? 0,
				});
			});
		}
	}

	private putDetail(machineId: string, read: AgentThreadDetail): void {
		const [detail] = this.withSettling(machineId, [read]);
		const key = threadKey(machineId, detail.id);
		const current = this.threads.get(key);
		const keep = current?.detail && !shouldReplaceDetail(current.detail, detail);
		this.threads.set(key, {
			...current,
			detail: keep && current?.detail ? current.detail : detail,
			loading: false,
			error: null,
			receivedAt: Date.now(),
		});
		// The list row follows the detail (status, needs you) without waiting for the next list.
		const list = this.lists.get(machineId);
		if (list?.threads) {
			const index = list.threads.findIndex((thread) => thread.id === detail.id);
			if (index !== -1) {
				const {items: _items, approvals: _approvals, revision: _revision, page: _page, ...summary} = detail;
				const threads = [...list.threads];
				threads[index] = {...threads[index], ...summary};
				this.lists.set(machineId, {...list, threads});
			}
		}
		const shown = this.threads.get(key)?.detail ?? detail;
		this.dropDelivered(machineId, detail.id, [
			...shown.items.filter((item) => item.kind === 'user'),
			// A prompt queued behind the running turn is delivered too: its bubble is the queued one.
			...(shown.queued ?? []).flatMap((queued) =>
				queued.messageId ? [{id: queued.messageId, text: queued.text}] : [],
			),
		]);
	}

	/**
	 * Threads as the person last settled and named them. A read that started before the change (a
	 * list refresh, a live update, the machine's own list before the tool caught up) would otherwise
	 * bring a settled thread back, or the old title, for a moment.
	 */
	private withSettling<T extends AgentThreadSummary>(machineId: string, threads: Array<T>): Array<T> {
		if (this.settling.size === 0 && this.renaming.size === 0 && this.dismissing.size === 0) return threads;
		const now = Date.now();
		return threads.map((thread) => {
			const key = threadKey(machineId, thread.id);
			let next = thread;
			const pin = this.settling.get(key);
			if (pin && now > pin.until) this.settling.delete(key);
			else if (pin && next.settled !== pin.settled) next = {...next, settled: pin.settled};
			const name = this.renaming.get(key);
			if (name && now > name.until) this.renaming.delete(key);
			else if (name && next.title !== name.title) next = {...next, title: name.title};
			const gone = this.dismissing.get(key);
			if (gone && now > gone.until) this.dismissing.delete(key);
			else if (gone) next = withoutQuestions(next, gone.ids);
			return next;
		});
	}

	/** The live list as the person last archived, unarchived and deleted in it. */
	private withArchiving(machineId: string, threads: Array<AgentThreadSummary>): Array<AgentThreadSummary> {
		if (this.archiving.size === 0) return threads;
		const now = Date.now();
		let next = threads;
		for (const [key, pin] of this.archiving) {
			if (now > pin.until) {
				this.archiving.delete(key);
				continue;
			}
			const [machine, threadId] = key.split('\u0000');
			if (machine !== machineId) continue;
			const listed = next.some((thread) => thread.id === threadId);
			if (pin.live === null && listed) next = next.filter((thread) => thread.id !== threadId);
			else if (pin.live && !listed) next = [...next, pin.live];
		}
		return next;
	}

	/** Older items of an open thread, before what the live read returned. */
	async loadEarlier(machineId: string, threadId: string): Promise<void> {
		const key = threadKey(machineId, threadId);
		const state = this.threads.get(key);
		const cursor = state?.earlierCursor === undefined ? state?.detail?.page.beforeCursor : state.earlierCursor;
		if (!state || !cursor || state.loadingEarlier) return;
		this.threads.set(key, {...state, loadingEarlier: true, earlierFailed: false});
		try {
			const response = answered(
				await http.get<AgentThreadDetail>(Endpoints.KONVO_AGENTS_THREAD(machineId, threadId), {
					query: {before: cursor},
					mode: 'silent',
				}),
			);
			runInAction(() => {
				const latest = this.threads.get(key);
				if (!latest) return;
				const known = new Set([...(latest.earlier ?? []), ...(latest.detail?.items ?? [])].map((item) => item.id));
				// Keep a rich answer from history even if its plain delivery is in the recent window.
				const earlierIds = new Set((latest.earlier ?? []).map((item) => item.id));
				const older = response.body.items.filter(
					(item) => !known.has(item.id) || (item.kind === 'user' && item.answers?.length && !earlierIds.has(item.id)),
				);
				this.threads.set(key, {
					...latest,
					earlier: [...older, ...(latest.earlier ?? [])],
					earlierCursor: response.body.page.hasMore ? (response.body.page.beforeCursor ?? null) : null,
					loadingEarlier: false,
				});
			});
		} catch {
			runInAction(() => {
				const latest = this.threads.get(key);
				if (latest) this.threads.set(key, {...latest, loadingEarlier: false, earlierFailed: true});
			});
		}
	}

	/** Ask the machine for live updates of these threads (renewed every 30 s while open). */
	async watch(machineId: string, threadIds: Array<string>): Promise<void> {
		try {
			await http.post(Endpoints.KONVO_AGENTS_WATCH(machineId), {body: {threadIds}, mode: 'silent'});
		} catch {
			// The 5 s refetch covers a missed watch.
		}
	}

	// ── Writes the person makes ──────────────────────────────────────────

	async sendMessage(
		machineId: string,
		threadId: string,
		prompt: string,
		steer = true,
		attachments: ReadonlyArray<AgentUploadInput> = [],
		/** Switches the thread to this model, reasoning level and service tier first. */
		model?: AgentModelChoice | null,
	): Promise<void> {
		const id = await this.post(
			machineId,
			threadId,
			prompt,
			[],
			model ?? null,
			false,
			undefined,
			undefined,
			steer,
			attachments,
		);
		await this.sendChains.get(threadKey(machineId, threadId));
		const failed = this.outgoing(id);
		if (failed?.state === 'failed') throw failed.error;
		await this.loadThread(machineId, threadId);
	}

	/** Where Konvo chat links may point: the web app, or this page when it runs elsewhere (desktop app, dev). */
	mentionOrigins(): Array<string> {
		const origins = [agentWebOrigin()];
		if (typeof location !== 'undefined' && location.origin && location.origin !== 'null') origins.push(location.origin);
		return origins;
	}

	/** A pull request link as the chats it belongs to know it. */
	knownPull(url: string): AgentPullRequest | null {
		for (const machine of this.machines)
			for (const thread of this.threadsOf(machine.id) ?? [])
				for (const pull of thread.pullRequests ?? []) if (pull.url === url) return pull;
		return null;
	}

	/** A prompt as sent: its mention links become `mention:<ref>` with what they point at (utils/AgentMentions). */
	private wire(prompt: string): {prompt: string; mentions?: Array<AgentMentionInput>} {
		const wired = mentionWire(prompt, this.mentionOrigins(), (url) => this.knownPull(url));
		return wired.mentions.length ? wired : {prompt};
	}

	/** The prompts on their way to this thread (or not sent), oldest first. */
	outgoingFor(machineId: string, threadId: string): Array<AgentOutgoing> {
		return this.outbox.filter((entry) => entry.machineId === machineId && entry.threadId === threadId);
	}

	/** The destination is decided at submit time, independently of delivery progress. */
	outgoingInChat(machineId: string, threadId: string): Array<AgentOutgoing> {
		return this.outgoingFor(machineId, threadId).filter((entry) => !entry.queue && !entry.holdUntil);
	}

	outgoingInQueue(machineId: string, threadId: string): Array<AgentOutgoing> {
		return this.outgoingFor(machineId, threadId).filter((entry) => entry.queue || entry.holdUntil);
	}

	/**
	 * Sends a prompt without waiting: it shows in its destination at once, goes once the prompts before it
	 * have, and stays on screen as not sent when it fails, to try again or take back to edit.
	 */
	async post(
		machineId: string,
		threadId: string,
		prompt: string,
		files: ReadonlyArray<File> = [],
		model: AgentModelChoice | null = null,
		/** Queue it behind the running turn rather than steer it in (Settings: what Enter does). */
		queue = false,
		holdUntil?: string,
		reason?: 'limit',
		steer = true,
		uploads: ReadonlyArray<AgentUploadInput> = [],
	): Promise<string> {
		const state = this.threads.get(threadKey(machineId, threadId));
		const before = [
			...[...(state?.earlier ?? []), ...(state?.detail?.items ?? [])]
				.filter((item) => item.kind === 'user')
				.map((item) => item.id),
			...(state?.detail?.queued ?? []).map((entry) => `queued:${entry.id}`),
		];
		const id = crypto.randomUUID();
		this.outbox.push({
			id,
			machineId,
			threadId,
			prompt,
			files,
			model,
			state: 'sending',
			phase: 'saving',
			error: null,
			holdUntil,
			steer,
			uploads,
			reason,
			before,
			...(queue ? {queue} : {}),
		});
		// A prompt brings a settled thread back; a settle made just before must not hold it there.
		this.settling.delete(threadKey(machineId, threadId));
		try {
			const entry = this.outgoing(id)!;
			await saveOutgoing(Users.currentUserId!, {kind: 'message', value: entry});
		} catch (error) {
			runInAction(() => {
				const entry = this.outgoing(id);
				if (entry) this.outbox.remove(entry);
			});
			throw error;
		}
		this.consumeEdited(threadDraftKey(machineId, threadId));
		this.deliver(id);
		return id;
	}

	retry(id: string): void {
		if (this.outgoing(id)?.state !== 'failed') return;
		this.patchOutgoing(id, {state: 'sending', error: null});
		this.deliver(id);
	}

	/** Takes a prompt that was not sent off the thread, to edit in the composer. */
	takeBack(id: string): AgentOutgoing | null {
		const entry = this.outgoing(id);
		if (!entry || entry.state !== 'failed') return null;
		this.rememberEditing(threadDraftKey(entry.machineId, entry.threadId), entry.id);
		void saveOutgoing(Users.currentUserId!, {kind: 'message', value: {...entry, editing: true}}).catch(() => undefined);
		this.outbox.remove(entry);
		return entry;
	}

	private outgoing(id: string): AgentOutgoing | null {
		return this.outbox.find((entry) => entry.id === id) ?? null;
	}

	private patchOutgoing(id: string, patch: Partial<AgentOutgoing>): void {
		const index = this.outbox.findIndex((entry) => entry.id === id);
		if (index !== -1) {
			this.outbox[index] = {...this.outbox[index], ...patch};
			void saveOutgoing(Users.currentUserId!, {kind: 'message', value: this.outbox[index]}).catch(() => undefined);
		}
	}

	private deliver(id: string): void {
		const entry = this.outgoing(id);
		if (!entry) return;
		const {machineId, threadId} = entry;
		const key = threadKey(machineId, threadId);
		const run = async () => {
			const deadline = Date.now() + RELEASE_GAP_PATIENCE_MS;
			for (;;) {
				const current = this.outgoing(id);
				if (!current || current.state !== 'sending') return;
				try {
					runInAction(() => this.patchOutgoing(id, {phase: current.files.length ? 'uploading' : 'linking'}));
					const attachments = current.files.length ? await uploadAgentFiles(current.files) : (current.uploads ?? []);
					if (!this.outgoing(id)) return;
					runInAction(() => this.patchOutgoing(id, {phase: 'linking'}));
					const response = await http.post<AgentQueuedSend>(
						current.holdUntil
							? Endpoints.KONVO_AGENTS_THREAD_QUEUED(machineId, threadId)
							: Endpoints.KONVO_AGENTS_THREAD_MESSAGES(machineId, threadId),
						{
							body: {
								sendId: current.id,
								...this.wire(current.prompt),
								...(current.holdUntil ? {at: current.holdUntil, reason: current.reason} : {}),
								...(current.queue ? {queue: true, steer: false} : {steer: current.steer ?? true}),
								...(attachments.length ? {attachments} : {}),
								...(current.model ? {model: current.model} : {}),
							},
						},
					);
					if (current.holdUntil) {
						runInAction(() => {
							this.queued = [
								...(this.queued ?? []).filter((q) => q.machineId !== machineId || q.threadId !== threadId),
								response.body,
							];
							const entry = this.outgoing(id);
							if (entry) this.outbox.remove(entry);
						});
						await deleteOutgoing(Users.currentUserId!, id);
						return;
					}
				} catch (error) {
					// Mid-release: wait for Konvo, then send again unless the thread shows it already
					// (a request cut off by the release may have reached the agent).
					if (isReleaseGap(error) && Date.now() < deadline && (await this.waitOutReleaseGap(id, deadline))) continue;
					runInAction(() => {
						if (this.outgoing(id)?.state === 'sending')
							this.patchOutgoing(id, {
								state: 'failed',
								phase:
									isReleaseGap(error) ||
									agentErrorCode(error) === 'MACHINE_TIMEOUT' ||
									(error instanceof Error && error.name === 'TimeoutError')
										? 'uncertain'
										: 'failed',
								error,
								failedAt: Date.now(),
							});
					});
					return;
				}
				break;
			}
			runInAction(() => this.patchOutgoing(id, {state: 'sent', phase: 'accepted'}));
			void this.loadThread(machineId, threadId);
		};
		const chain = (this.sendChains.get(key) ?? Promise.resolve()).then(run);
		this.sendChains.set(key, chain);
		void chain.finally(() => {
			if (this.sendChains.get(key) === chain) this.sendChains.delete(key);
		});
	}

	/**
	 * Waits until the thread can be read again (Konvo is back), up to the deadline. True: send the
	 * prompt again. False: it is no longer waiting (the thread shows it, or it was taken back).
	 */
	private async waitOutReleaseGap(id: string, deadline: number): Promise<boolean> {
		while (Date.now() < deadline) {
			await new Promise((resolve) => setTimeout(resolve, RELEASE_GAP_RETRY_MS));
			const entry = this.outgoing(id);
			if (!entry || entry.state !== 'sending') return false;
			await this.loadThread(entry.machineId, entry.threadId);
			// The read drops prompts the thread shows (dropDelivered).
			if (!this.outgoing(id)) return false;
			const read = this.threads.get(threadKey(entry.machineId, entry.threadId));
			if (!read?.error) return true;
			// Still mid-release, or Konvo is back and the machine is a moment from polling it again.
			if (!isReleaseGap(read.error) && agentErrorCode(read.error) !== 'MACHINE_OFFLINE') return true;
		}
		return false;
	}

	/** Delivery is acknowledged by identity, including after a request failed or timed out. */
	private dropDelivered(machineId: string, threadId: string, prompts: ReadonlyArray<{id: string; text: string}>): void {
		const ids = new Set(prompts.map((prompt) => prompt.id));
		for (const entry of [...this.outbox]) {
			if (entry.machineId !== machineId || entry.threadId !== threadId || !ids.has(entry.id)) continue;
			this.outbox.remove(entry);
			void deleteOutgoing(Users.currentUserId!, entry.id).catch(() => undefined);
			if (entry.startId) void deleteOutgoing(Users.currentUserId!, entry.startId).catch(() => undefined);
		}
	}

	/** Where a thread sent to an agent came from (null: started in Agents), per machine and thread. */
	readonly sources = observable.map<string, AgentThreadSource | null>();
	/** Where each chat went on in the cloud (Continue in the cloud), read with its source. */
	readonly continued = observable.map<string, AgentContinuedIn | null>();

	async loadSource(machineId: string, threadId: string): Promise<void> {
		const key = threadKey(machineId, threadId);
		if (this.sources.has(key)) return;
		try {
			const response = answered(
				await http.get<{source: AgentThreadSource | null; continuedIn?: AgentContinuedIn | null}>(
					Endpoints.KONVO_AGENTS_THREAD_SOURCE(machineId, threadId),
					{mode: 'silent'},
				),
			);
			runInAction(() => {
				this.sources.set(key, response.body.source);
				this.continued.set(key, response.body.continuedIn ?? null);
			});
		} catch {
			// Nothing to offer; the next open tries again.
		}
	}

	/** The cloud chat a chat went on in (Continue in the cloud), once loadSource has read it. */
	continuedInOf(machineId: string, threadId: string): AgentContinuedIn | null {
		return this.continued.get(threadKey(machineId, threadId)) ?? null;
	}

	/** What Continue in the cloud will do for a chat, or one of its messages (konvo-agents 0.55.0+). */
	async previewCloudHandoff(
		machineId: string,
		threadId: string,
		messageId?: string,
	): Promise<AgentCloudHandoffPreview> {
		const response = answered(
			await http.get<AgentCloudHandoffPreview>(Endpoints.KONVO_AGENTS_CLOUD_HANDOFF(machineId, threadId), {
				query: messageId ? {message: messageId} : {},
				mode: 'silent',
			}),
		);
		return response.body;
	}

	/** Hands a chat (or one message) over to a new cloud chat: push, start, link. Returns the new chat. */
	async continueInCloud(
		machineId: string,
		threadId: string,
		body: {messageId?: string; model?: AgentModelChoice},
	): Promise<{threadId: string; branch: string}> {
		const response = answered(
			await http.post<{threadId: string; branch: string}>(Endpoints.KONVO_AGENTS_CLOUD_HANDOFF(machineId, threadId), {
				body,
				mode: 'silent',
			}),
		);
		const started = response.body;
		runInAction(() =>
			this.continued.set(threadKey(machineId, threadId), {
				threadId: started.threadId,
				branch: started.branch,
				at: new Date().toISOString(),
			}),
		);
		void this.loadThreads(machineId);
		return started;
	}

	/**
	 * Makes a project for a chat in a folder of its own (konvo-agents 0.61.0+): a No project chat moves
	 * into it, any other continues there as a new chat. Returns the chat that goes on.
	 */
	async chatToProject(
		machineId: string,
		threadId: string,
		body: {title: string; path: string},
	): Promise<{threadId: string; projectId: string; moved: boolean}> {
		const response = answered(
			await http.post<{threadId: string; projectId: string; moved: boolean}>(
				Endpoints.KONVO_AGENTS_THREAD_PROJECT(machineId, threadId),
				{body, mode: 'silent'},
			),
		);
		const done = response.body;
		runInAction(() =>
			this.continued.set(threadKey(machineId, threadId), {
				kind: done.moved ? 'moved' : 'project',
				threadId: done.threadId,
				project: body.title,
				path: body.path,
				at: new Date().toISOString(),
			}),
		);
		// The machine shares the new project; the list and the chat's own project follow.
		void this.loadThreads(machineId);
		if (done.moved) void this.loadThread(machineId, threadId);
		return done;
	}

	sourceOf(machineId: string, threadId: string): AgentThreadSource | null {
		return this.sources.get(threadKey(machineId, threadId)) ?? null;
	}

	/**
	 * A project's local branches for a new chat to start from (konvo-agents 0.11.0+); with switchTo,
	 * also why the checkout couldn't switch to that branch (switchProblem, 0.41.0+); with cloudFrom,
	 * how many of that branch's commits GitHub doesn't have yet (unpushed, 0.54.0+).
	 */
	async loadBranches(
		machineId: string,
		projectId: string,
		switchTo?: string,
		cloudFrom?: string,
	): Promise<AgentBranches> {
		const response = answered(
			await http.get<AgentBranches>(Endpoints.KONVO_AGENTS_BRANCHES(machineId), {
				query: {project: projectId, ...(switchTo ? {switchTo} : {}), ...(cloudFrom ? {cloudFrom} : {})},
				mode: 'silent',
			}),
		);
		return response.body;
	}

	/** Pushes a project's branch to GitHub before a cloud chat starts from it (konvo-agents 0.54.0+). */
	async pushBranch(machineId: string, projectId: string, branch: string): Promise<void> {
		answered(
			await http.post(Endpoints.KONVO_AGENTS_PROJECT_PUSH(machineId, projectId), {body: {branch}, mode: 'silent'}),
		);
	}

	/** Opens a thread at a message a search found (the thread view scrolls to it). */
	focusOn(machineId: string, threadId: string, at: string): void {
		this.focusMessage = {machineId, threadId, at};
	}

	clearFocus(): void {
		this.focusMessage = null;
	}

	/** What turns `from` (exclusive) to `to` changed; from 0 is the whole thread. `file`: one file's patch (konvo-agents 0.25.0+). */
	async loadDiff(machineId: string, threadId: string, from: number, to: number, file?: string): Promise<AgentDiff> {
		const response = answered(
			await http.get<AgentDiff>(Endpoints.KONVO_AGENTS_DIFF(machineId, threadId), {
				query: {from: String(from), to: String(to), ...(file !== undefined ? {file} : {})},
				mode: 'silent',
			}),
		);
		return response.body;
	}

	/** Messages in the machine's shared threads that match the query (konvo-agents 0.25.0+). */
	async search(machineId: string, query: string): Promise<AgentSearch> {
		const response = answered(
			await http.get<AgentSearch>(Endpoints.KONVO_AGENTS_SEARCH(machineId), {
				query: {q: query},
				mode: 'silent',
			}),
		);
		return response.body;
	}

	async checkAgentSetup(machineId: string, args: AgentSetupRequest): Promise<AgentToolInfo> {
		const response = await http.post<AgentToolInfo>(Endpoints.KONVO_AGENTS_SETUP(machineId), {body: args});
		runInAction(() => {
			this.machines = this.machines.map((machine) =>
				machine.id === machineId
					? {...machine, tools: machine.tools.map((tool) => (tool.tool === response.body.tool ? response.body : tool))}
					: machine,
			);
		});
		return response.body;
	}

	/**
	 * Adds, edits, turns off or removes a Claude or Codex account on the owner's machine (the account
	 * op, konvo-agents 0.51.0+). The machine answers with its tool, accounts included; the overview is
	 * read again for the health the API works out from it.
	 */
	async saveAccount(machineId: string, request: AgentAccountRequest): Promise<AgentToolInfo> {
		const response = await http.post<AgentToolInfo>(Endpoints.KONVO_AGENTS_ACCOUNTS(machineId), {
			body: request,
			timeoutMs: 65_000,
		});
		runInAction(() => {
			this.machines = this.machines.map((machine) =>
				machine.id === machineId
					? {
							...machine,
							tools: machine.tools.map((tool) =>
								tool.tool === response.body.tool ? {...response.body, can: {...tool.can, ...response.body.can}} : tool,
							),
						}
					: machine,
			);
		});
		void this.loadOverview();
		return response.body;
	}

	// ── Agent tool updates ───────────────────────────────────────────────

	/** A machine's Claude Code and Codex update rows at `now` (the usage dock, machine menu, phone list). */
	toolUpdates(machine: AgentMachineView, now: number): Array<AgentToolUpdate> {
		return agentToolUpdates(machine, now, this.updateAsks, this.updateEnds);
	}

	private noteUpdateEnds(before: Array<AgentMachineView>, after: Array<AgentMachineView>): void {
		for (const [key, end] of endedAgentUpdates(before, after, this.updateAsks, Date.now())) {
			this.updateEnds.set(key, end);
			if (!this.updateAsks.get(key)?.error) this.updateAsks.delete(key);
		}
	}

	/**
	 * Updates Claude Code or Codex on the person's own machine. The machine answers at once and the
	 * update runs on there; its summary says how it ended. A refusal shows on the row.
	 */
	async updateAgentTool(machineId: string, provider: AgentToolProvider): Promise<void> {
		const key = agentUpdateKey(machineId, provider);
		const current = this.machine(machineId);
		if (
			current &&
			this.toolUpdates(current, Date.now()).some((row) => row.provider === provider && row.phase === 'updating')
		)
			return;
		this.updateAsks.set(key, {at: Date.now()});
		try {
			const response = answered(
				await http.post<AgentToolInfo>(Endpoints.KONVO_AGENTS_AGENT_UPDATE(machineId), {
					body: {provider},
					timeoutMs: 65_000,
				}),
			);
			runInAction(() => {
				// The machine took it up: its summary says how it goes from here.
				this.updateEnds.delete(key);
				const before = this.machines;
				this.machines = this.machines.map((machine) =>
					machine.id === machineId
						? {
								...machine,
								tools: machine.tools.map((tool) =>
									tool.tool === response.body.tool
										? {...response.body, can: {...tool.can, ...response.body.can}}
										: tool,
								),
							}
						: machine,
				);
				this.noteUpdateEnds(before, this.machines);
				this.updateAsks.delete(key);
			});
		} catch (error) {
			const machine = this.machine(machineId)?.name ?? '';
			const code = agentErrorCode(error);
			const said = (error as {body?: {message?: unknown}} | null)?.body?.message;
			const message =
				code === 'MACHINE_OFFLINE'
					? i18n._(UPDATE_OFFLINE_DESCRIPTOR, {machine})
					: code === 'MACHINE_READ_ONLY'
						? i18n._(UPDATE_READ_ONLY_DESCRIPTOR, {machine})
						: code === 'BRIDGE_TOO_OLD'
							? i18n._(UPDATE_BRIDGE_OLD_DESCRIPTOR, {machine})
							: code === 'MACHINE_TIMEOUT'
								? i18n._(UPDATE_TIMEOUT_DESCRIPTOR, {machine})
								: // The machine's own words (T3 cannot update it here, already updating, ...).
									code && typeof said === 'string' && said.trim()
									? said.trim().slice(0, 400)
									: i18n._(UPDATE_FAILED_DESCRIPTOR);
			runInAction(() => {
				this.updateAsks.set(key, {at: Date.now(), error: message});
			});
		}
	}

	async startThread(machineId: string, args: StartThreadArgs): Promise<string> {
		const response = await http.post<{threadId: string}>(Endpoints.KONVO_AGENTS_THREADS(machineId), {
			body: {...args, ...this.wire(args.prompt)},
		});
		void this.loadThreads(machineId);
		return response.body.threadId;
	}

	/**
	 * Starts a new chat without waiting: its page and row show at once, and it keeps going when the
	 * person goes elsewhere. Returns the id its page opens under (in the thread's place).
	 */
	async startPending(
		input: Omit<AgentPendingStart, 'id' | 'startedAt' | 'state' | 'error' | 'threadId'>,
	): Promise<string> {
		const id = `${PENDING_THREAD_PREFIX}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
		this.pendingStarts.push({
			...input,
			id,
			startedAt: new Date().toISOString(),
			state: 'starting',
			error: null,
			threadId: null,
		});
		const entry = this.pendingStart(id)!;
		entry.args = {...entry.args, sendId: crypto.randomUUID()};
		try {
			await saveOutgoing(Users.currentUserId!, {kind: 'start', value: entry});
		} catch (error) {
			runInAction(() => this.pendingStarts.remove(entry));
			throw error;
		}
		this.consumeEdited(NEW_CHAT_DRAFT);
		void this.runStart(id);
		return id;
	}

	pendingStart(id: string): AgentPendingStart | null {
		return this.pendingStarts.find((entry) => entry.id === id) ?? null;
	}

	/** Tries a chat that didn't start again, as it was or with a change (StartChange). */
	retryStart(id: string, change?: StartChange): void {
		const entry = this.pendingStart(id);
		if (entry?.state !== 'failed' && entry?.state !== 'waiting') return;
		this.stopCommitWait(id);
		const args = {...entry.args};
		if (change) {
			if (change.worktree !== undefined) args.worktree = change.worktree;
			if (change.branch === null) delete args.branch;
			else if (change.branch !== undefined) args.branch = change.branch;
			if (change.stash) args.stash = true;
			else delete args.stash;
		}
		this.patchStart(id, {
			state: 'starting',
			error: null,
			args,
			waitingOn: undefined,
			...(change ? {branch: change.shownBranch} : {}),
		});
		void this.runStart(id);
	}

	/**
	 * Asks the chat that has the checkout's changes to commit them; this one starts by itself once
	 * that chat's turn is over (while Konvo is open; Try again works too).
	 */
	async askToCommit(id: string, thread: {id: string; title: string}, prompt: string): Promise<void> {
		const entry = this.pendingStart(id);
		if (!entry || entry.state !== 'failed') return;
		const {machineId} = entry;
		await this.sendMessage(machineId, thread.id, prompt);
		const askedAt = Date.now();
		runInAction(() => this.patchStart(id, {state: 'waiting', waitingOn: {threadId: thread.id, title: thread.title}}));
		const done = (summary: AgentThreadSummary | null) =>
			Boolean(
				summary &&
					summary.status !== 'running' &&
					!summary.needsYou &&
					summary.turnFinishedAt &&
					Date.parse(summary.turnFinishedAt) >= askedAt - 1000,
			);
		const stopWatch = reaction(
			() => this.summary(machineId, thread.id),
			(summary) => {
				if (done(summary)) this.retryStart(id);
			},
		);
		const poll = setInterval(() => void this.loadThreads(machineId), COMMIT_POLL_MS);
		const giveUp = setTimeout(
			() =>
				runInAction(() => {
					this.stopCommitWait(id);
					if (this.pendingStart(id)?.state === 'waiting') this.patchStart(id, {state: 'failed', waitingOn: undefined});
				}),
			COMMIT_WAIT_MS,
		);
		commitWaits.set(id, () => {
			stopWatch();
			clearInterval(poll);
			clearTimeout(giveUp);
		});
	}

	/** Stops waiting for the chat asked to commit (it is tried now, edited or gone). */
	stopCommitWait(id: string): void {
		commitWaits.get(id)?.();
		commitWaits.delete(id);
	}

	/** Takes a chat that did not start off the list, to edit in the new chat page. */
	takeBackStart(id: string): AgentPendingStart | null {
		const entry = this.pendingStart(id);
		if (!entry || (entry.state !== 'failed' && entry.state !== 'waiting')) return null;
		this.stopCommitWait(id);
		this.pendingStarts.remove(entry);
		this.rememberEditing(NEW_CHAT_DRAFT, id);
		void saveOutgoing(Users.currentUserId!, {kind: 'start', value: {...entry, editing: true}}).catch(() => undefined);
		return entry;
	}

	/** Rows for new chats the machine's list doesn't show yet, as threads of their projects. */
	pendingRows(machineId: string): Array<AgentThreadSummary> {
		const listed = new Set((this.lists.get(machineId)?.threads ?? []).map((thread) => thread.id));
		return this.pendingStarts
			.filter((entry) => entry.machineId === machineId && !(entry.threadId && listed.has(entry.threadId)))
			.map((entry) => ({
				id: entry.id,
				projectId: entry.args.projectId,
				title: pendingTitle(entry.typed || entry.args.prompt),
				status: entry.state === 'failed' ? ('error' as const) : ('running' as const),
				needsYou: false,
				provider: entry.provider ?? 'unknown',
				...(entry.modelName ? {model: entry.modelName} : {}),
				...(entry.branch ? {branch: entry.branch} : {}),
				updatedAt: entry.startedAt,
				turnStartedAt: entry.startedAt,
			}));
	}

	private patchStart(id: string, patch: Partial<AgentPendingStart>): void {
		const index = this.pendingStarts.findIndex((entry) => entry.id === id);
		if (index !== -1) {
			this.pendingStarts[index] = {...this.pendingStarts[index], ...patch};
			void saveOutgoing(Users.currentUserId!, {kind: 'start', value: this.pendingStarts[index]}).catch(() => undefined);
		}
	}

	private async runStart(id: string): Promise<void> {
		const entry = this.pendingStart(id);
		if (!entry) return;
		const {machineId} = entry;
		let threadId: string;
		try {
			const attachments = entry.files.length ? await uploadAgentFiles(entry.files) : [];
			if (!this.pendingStart(id)) return;
			threadId = await this.startThread(machineId, {...entry.args, ...(attachments.length ? {attachments} : {})});
		} catch (error) {
			runInAction(() => {
				if (this.pendingStart(id)?.state === 'starting') this.patchStart(id, {state: 'failed', error});
			});
			return;
		}
		// The thread's page takes over without a gap: the prompt shows from here until the thread's own
		// copy does (then it goes, so there is never two), and the thread is read before the page swaps.
		if (!this.pendingStart(id)) return;
		const outgoingId = entry.args.sendId!;
		runInAction(() => {
			// Known now, so the list never shows this row and the thread's own at once.
			this.patchStart(id, {threadId});
			this.outbox.push({
				id: outgoingId,
				startId: id,
				machineId,
				threadId,
				prompt: entry.args.prompt,
				files: entry.files,
				model: null,
				state: 'sent',
				error: null,
				before: [],
			});
		});
		await saveOutgoing(Users.currentUserId!, {kind: 'message', value: this.outgoing(outgoingId)!}).catch(
			() => undefined,
		);
		await Promise.race([
			this.loadThread(machineId, threadId),
			new Promise((resolve) => setTimeout(resolve, START_PRELOAD_MS)),
		]);
		runInAction(() => {
			this.patchStart(id, {state: 'started', threadId, doneAt: Date.now()});
			this.forgetStarted(machineId);
		});
	}

	/** The chat's page became the thread: it no longer needs the entry. */
	forgetStart(id: string): void {
		const entry = this.pendingStart(id);
		if (entry?.state === 'started') {
			this.pendingStarts.remove(entry);
			if (!this.outgoing(entry.args.sendId!)) void deleteOutgoing(Users.currentUserId!, id).catch(() => undefined);
		}
	}

	/**
	 * Started chats the list shows are forgotten a while after they started (their row is already the
	 * thread's; an open page has swapped to it and forgot it itself).
	 */
	private forgetStarted(machineId: string): void {
		if (this.pendingStarts.length === 0) return;
		const listed = new Set((this.lists.get(machineId)?.threads ?? []).map((thread) => thread.id));
		const before = Date.now() - START_FORGET_MS;
		for (const entry of [...this.pendingStarts])
			if (
				entry.machineId === machineId &&
				entry.state === 'started' &&
				entry.threadId &&
				listed.has(entry.threadId) &&
				(entry.doneAt ?? 0) < before
			) {
				this.pendingStarts.remove(entry);
				if (!this.outgoing(entry.args.sendId!))
					void deleteOutgoing(Users.currentUserId!, entry.id).catch(() => undefined);
			}
	}

	async interrupt(machineId: string, threadId: string): Promise<void> {
		await http.post(Endpoints.KONVO_AGENTS_THREAD_INTERRUPT(machineId, threadId), {body: {}});
		void this.loadThread(machineId, threadId);
	}

	/** A prompt waiting in T3's queue (can.queue): sent now as a steer, or taken off the queue. */
	queueActionPending(machineId: string, threadId: string, runId: string): boolean {
		return this.queueActions.has(`${threadKey(machineId, threadId)}\u0000${runId}`);
	}

	async queuedPrompt(machineId: string, threadId: string, runId: string, action: 'steer' | 'cancel'): Promise<void> {
		const key = `${threadKey(machineId, threadId)}\u0000${runId}`;
		if (this.queueActions.has(key)) return;
		this.queueActions.add(key);
		const prompt = this.thread(machineId, threadId)?.detail?.queued?.find((entry) => entry.id === runId);
		try {
			await http.post(Endpoints.KONVO_AGENTS_THREAD_QUEUE(machineId, threadId), {body: {runId, action}});
			runInAction(() => {
				const state = this.threads.get(threadKey(machineId, threadId));
				if (!state?.detail) return;
				this.threads.set(threadKey(machineId, threadId), {
					...state,
					detail: {...state.detail, queued: state.detail.queued?.filter((entry) => entry.id !== runId)},
				});
				if (
					action === 'steer' &&
					prompt?.messageId &&
					!state.detail.items.some((item) => item.id === prompt.messageId) &&
					!this.outgoing(prompt.messageId)
				) {
					const entry: AgentOutgoing = {
						id: prompt.messageId,
						machineId,
						threadId,
						prompt: prompt.text || prompt.attachments?.map((file) => file.name).join(' · ') || '',
						files: [],
						model: null,
						state: 'sent',
						phase: 'accepted',
						error: null,
						before: [],
					};
					this.outbox.push(entry);
					void saveOutgoing(Users.currentUserId!, {kind: 'message', value: entry}).catch(() => undefined);
				}
			});
		} finally {
			runInAction(() => this.queueActions.delete(key));
			void this.loadThread(machineId, threadId);
		}
	}

	/** Stops a dev server the chat started in one of its terminals (can.stopDevServer): Ctrl+C there. */
	async stopDevServer(machineId: string, threadId: string, terminalId: string): Promise<void> {
		await http.post(Endpoints.KONVO_AGENTS_THREAD_DEV_SERVER_STOP(machineId, threadId), {body: {terminalId}});
	}

	/** Sums up the chat so far to free the agent's context (can.compact): a turn the thread shows running. */
	async compact(machineId: string, threadId: string): Promise<void> {
		await http.post(Endpoints.KONVO_AGENTS_THREAD_COMPACT(machineId, threadId), {body: {}});
		void this.loadThread(machineId, threadId);
	}

	/**
	 * Rewinds the chat to before one of its prompts (can.rewind); restoreFiles puts its files back too.
	 * The prompt goes back in the composer, ahead of anything already typed there.
	 */
	async rewind(machineId: string, threadId: string, messageId: string, restoreFiles: boolean): Promise<void> {
		const response = await http.post<{accepted: true; prompt: string; done: boolean}>(
			Endpoints.KONVO_AGENTS_THREAD_REWIND(machineId, threadId),
			{body: {messageId, restoreFiles}, timeoutMs: 90_000},
		);
		const prompt = response.body.prompt.trim();
		if (prompt) {
			const key = threadDraftKey(machineId, threadId);
			const current = AgentDrafts.get(key).trim();
			AgentDrafts.set(key, current ? `${prompt}\n\n${current}` : prompt);
		}
		void this.loadThread(machineId, threadId);
	}

	/** Restarts the chat's agent process so new skills and MCP servers load (can.restartSession). */
	async restartSession(machineId: string, threadId: string): Promise<void> {
		await http.post(Endpoints.KONVO_AGENTS_THREAD_RESTART(machineId, threadId), {body: {}});
		void this.loadThread(machineId, threadId);
	}

	/** Files a thread under Settled, or brings it back. The list moves at once; a failure puts it back. */
	async settle(machineId: string, threadId: string, settled: boolean): Promise<void> {
		const key = threadKey(machineId, threadId);
		const list = this.lists.get(machineId);
		const before = {list, state: this.threads.get(key)};
		this.settling.set(key, {settled, until: Number.POSITIVE_INFINITY});
		runInAction(() => {
			if (list?.threads) {
				const threads = list.threads.map((thread) => (thread.id === threadId ? {...thread, settled} : thread));
				this.lists.set(machineId, {...list, threads});
			}
			if (before.state?.detail) this.threads.set(key, {...before.state, detail: {...before.state.detail, settled}});
		});
		try {
			await http.post(Endpoints.KONVO_AGENTS_THREAD_SETTLE(machineId, threadId), {body: {settled}});
		} catch (error) {
			if (this.settling.get(key)?.settled === settled) this.settling.delete(key);
			runInAction(() => {
				const latest = this.lists.get(machineId);
				const was = list?.threads?.find((thread) => thread.id === threadId);
				if (latest?.threads && was) {
					const threads = latest.threads.map((thread) =>
						thread.id === threadId ? {...thread, settled: was.settled} : thread,
					);
					this.lists.set(machineId, {...latest, threads});
				}
				if (before.state) this.threads.set(key, before.state);
			});
			throw error;
		}
		// Settled from Konvo: a pinned chat goes under Settled too (the tool settling on its own leaves it pinned).
		if (settled && this.isPinned(machineId, threadId))
			void this.setPinned(machineId, threadId, false).catch(() => undefined);
		// The machine has it; give the tool a moment to say so in its own lists.
		const pin = this.settling.get(key);
		if (pin?.settled === settled && pin.until === Number.POSITIVE_INFINITY) pin.until = Date.now() + SETTLE_GRACE_MS;
		void this.loadThreads(machineId);
	}

	/** Gives a thread a new title in its agent tool. The new title shows at once; a failure puts the old one back. */
	async renameThread(machineId: string, threadId: string, title: string): Promise<void> {
		const key = threadKey(machineId, threadId);
		const before = this.summary(machineId, threadId)?.title ?? this.threads.get(key)?.detail?.title;
		this.renaming.set(key, {title, until: Number.POSITIVE_INFINITY});
		this.showTitle(machineId, threadId, title);
		try {
			await http.post(Endpoints.KONVO_AGENTS_THREAD_RENAME(machineId, threadId), {body: {title}});
		} catch (error) {
			if (this.renaming.get(key)?.title === title) {
				this.renaming.delete(key);
				if (before !== undefined) this.showTitle(machineId, threadId, before);
			}
			throw error;
		}
		// The machine has it; give the tool a moment to say so in its own lists.
		const pin = this.renaming.get(key);
		if (pin?.title === title && pin.until === Number.POSITIVE_INFINITY) pin.until = Date.now() + SETTLE_GRACE_MS;
	}

	private showTitle(machineId: string, threadId: string, title: string): void {
		const list = this.lists.get(machineId);
		if (list?.threads) {
			const threads = list.threads.map((thread) => (thread.id === threadId ? {...thread, title} : thread));
			this.lists.set(machineId, {...list, threads});
		}
		const state = this.threads.get(threadKey(machineId, threadId));
		if (state?.detail) this.threads.set(threadKey(machineId, threadId), {...state, detail: {...state.detail, title}});
	}

	// ── Archive and delete (konvo-agents 0.36.0+) ─────────────────────────

	/** Reads the machine's archived threads; keeps the last ones while a read fails. */
	async loadArchived(machineId: string): Promise<void> {
		const current = this.archived.get(machineId);
		this.archived.set(machineId, {threads: current?.threads ?? null, loading: true, error: null});
		try {
			const response = answered(
				await http.get<{threads: Array<AgentThreadSummary>}>(Endpoints.KONVO_AGENTS_ARCHIVED(machineId), {
					mode: 'silent',
				}),
			);
			runInAction(() => {
				const now = Date.now();
				// What the person just changed wins over a read from before the machine had it.
				let threads = response.body.threads ?? [];
				for (const [key, pin] of this.archiving) {
					const [machine, threadId] = key.split('\u0000');
					if (machine !== machineId || now > pin.until) continue;
					const listed = threads.some((thread) => thread.id === threadId);
					const kept = current?.threads?.find((thread) => thread.id === threadId);
					if (pin.live && listed) threads = threads.filter((thread) => thread.id !== threadId);
					else if (!pin.live && !listed && kept) threads = [...threads, kept];
				}
				this.archived.set(machineId, {threads, loading: false, error: null});
			});
		} catch (error) {
			runInAction(() => {
				this.archived.set(machineId, {threads: this.archived.get(machineId)?.threads ?? null, loading: false, error});
			});
		}
	}

	/**
	 * Archives a thread, or brings it back. It moves between the live and archived lists at once;
	 * a failure puts it back.
	 */
	async archive(machineId: string, threadId: string, archived: boolean): Promise<void> {
		const key = threadKey(machineId, threadId);
		const before = {
			list: this.lists.get(machineId),
			archived: this.archived.get(machineId),
			state: this.threads.get(key),
		};
		const detail = before.state?.detail;
		const found = this.summary(machineId, threadId) ?? (detail ? summaryOf(detail) : null);
		const live = found ? withoutArchivedAt(found) : null;
		const archivedAt = new Date().toISOString();
		this.archiving.set(key, {live: archived ? null : live, until: Number.POSITIVE_INFINITY});
		runInAction(() => {
			const list = before.list;
			if (list?.threads) {
				const rest = list.threads.filter((thread) => thread.id !== threadId);
				this.lists.set(machineId, {...list, threads: !archived && live ? [...rest, live] : rest});
			}
			const others = (before.archived?.threads ?? []).filter((thread) => thread.id !== threadId);
			if (before.archived || archived)
				this.archived.set(machineId, {
					threads: archived && live ? [{...live, archivedAt}, ...others] : others,
					loading: before.archived?.loading ?? false,
					error: null,
				});
			if (before.state && detail) {
				const next = withoutArchivedAt(detail);
				this.threads.set(key, {...before.state, detail: archived ? {...next, archivedAt} : next});
			}
		});
		try {
			await http.post(Endpoints.KONVO_AGENTS_THREAD_ARCHIVE(machineId, threadId), {body: {archived}});
		} catch (error) {
			this.archiving.delete(key);
			runInAction(() => {
				if (before.list) this.lists.set(machineId, before.list);
				if (before.archived) this.archived.set(machineId, before.archived);
				else this.archived.delete(machineId);
				if (before.state) this.threads.set(key, before.state);
			});
			throw error;
		}
		const pin = this.archiving.get(key);
		if (pin?.until === Number.POSITIVE_INFINITY) pin.until = Date.now() + SETTLE_GRACE_MS;
		void this.loadThreads(machineId);
		void this.loadArchived(machineId);
	}

	/** Deletes a thread from its machine for good. It leaves the lists at once; a failure puts it back. */
	async deleteThread(machineId: string, threadId: string): Promise<void> {
		const key = threadKey(machineId, threadId);
		const before = {list: this.lists.get(machineId), archived: this.archived.get(machineId)};
		this.archiving.set(key, {live: null, until: Number.POSITIVE_INFINITY});
		runInAction(() => {
			if (before.list?.threads)
				this.lists.set(machineId, {
					...before.list,
					threads: before.list.threads.filter((thread) => thread.id !== threadId),
				});
			if (before.archived?.threads)
				this.archived.set(machineId, {
					...before.archived,
					threads: before.archived.threads.filter((thread) => thread.id !== threadId),
				});
		});
		try {
			await http.delete(Endpoints.KONVO_AGENTS_THREAD(machineId, threadId));
		} catch (error) {
			this.archiving.delete(key);
			runInAction(() => {
				if (before.list) this.lists.set(machineId, before.list);
				if (before.archived) this.archived.set(machineId, before.archived);
			});
			throw error;
		}
		const pin = this.archiving.get(key);
		if (pin) pin.until = Date.now() + SETTLE_GRACE_MS;
		runInAction(() => {
			this.threads.delete(key);
			if (this.listRename?.machineId === machineId && this.listRename.threadId === threadId) this.listRename = null;
		});
	}

	/** Allow or deny an approval request. Hides it at once; a failure brings it back. */
	async respond(machineId: string, threadId: string, requestId: string, decision: AgentDecision): Promise<void> {
		const key = threadKey(machineId, threadId);
		const before = this.threads.get(key);
		if (before?.detail) {
			this.threads.set(key, {
				...before,
				detail: {
					...before.detail,
					approvals: before.detail.approvals.filter((approval) => approval.requestId !== requestId),
				},
			});
		}
		try {
			await http.post(Endpoints.KONVO_AGENTS_THREAD_APPROVAL(machineId, threadId, requestId), {body: {decision}});
		} catch (error) {
			runInAction(() => {
				if (before) this.threads.set(key, before);
			});
			throw error;
		} finally {
			void this.loadThread(machineId, threadId);
		}
	}

	/** Keep the disabled card mounted until accepted, so a failed send retains its text and photos. */
	async answer(
		machineId: string,
		threadId: string,
		requestId: string,
		answers: AgentAnswers,
		attachmentsByQuestionId?: AgentQuestionUploads,
	): Promise<void> {
		const key = threadKey(machineId, threadId);
		try {
			await http.post(Endpoints.KONVO_AGENTS_THREAD_QUESTION(machineId, threadId, requestId), {
				body: {
					answers,
					...(attachmentsByQuestionId && Object.keys(attachmentsByQuestionId).length ? {attachmentsByQuestionId} : {}),
				},
			});
			runInAction(() => {
				const current = this.threads.get(key);
				if (current?.detail)
					this.threads.set(key, {
						...current,
						detail: {
							...current.detail,
							questions: (current.detail.questions ?? []).filter((question) => question.requestId !== requestId),
						},
					});
			});
		} finally {
			void this.loadThread(machineId, threadId);
		}
	}

	/**
	 * Gives the agent tool the secret it asked for, or declines. The card leaves once the machine
	 * took it; on a failure it stays, so the person can try again.
	 */
	async answerSecret(machineId: string, threadId: string, requestId: string, answer: AgentSecretAnswer): Promise<void> {
		const key = threadKey(machineId, threadId);
		try {
			await http.post(Endpoints.KONVO_AGENTS_THREAD_SECRET(machineId, threadId, requestId), {body: {answer}});
			runInAction(() => {
				const current = this.threads.get(key);
				if (current?.detail?.secrets)
					this.threads.set(key, {
						...current,
						detail: {
							...current.detail,
							secrets: current.detail.secrets.filter((secret) => secret.requestId !== requestId),
						},
					});
			});
		} finally {
			void this.loadThread(machineId, threadId);
		}
	}

	/**
	 * Closes the agent's question without an answer. It leaves the thread, the list and the badges
	 * at once; a failure brings it back. `stopped`: the turn waited on it and was stopped.
	 */
	async dismiss(machineId: string, threadId: string, requestId: string): Promise<{stopped: boolean}> {
		const key = threadKey(machineId, threadId);
		const list = this.lists.get(machineId);
		const before = {row: list?.threads?.find((thread) => thread.id === threadId), state: this.threads.get(key)};
		const pin = this.dismissing.get(key);
		if (pin) {
			pin.ids.add(requestId);
			pin.until = Number.POSITIVE_INFINITY;
		} else this.dismissing.set(key, {ids: new Set([requestId]), until: Number.POSITIVE_INFINITY});
		const ids = new Set([requestId]);
		runInAction(() => {
			if (list?.threads) {
				const threads = list.threads.map((thread) => (thread.id === threadId ? withoutQuestions(thread, ids) : thread));
				this.lists.set(machineId, {...list, threads});
			}
			if (before.state?.detail)
				this.threads.set(key, {...before.state, detail: withoutQuestions(before.state.detail, ids)});
		});
		try {
			const response = await http.post<{accepted: true; stopped?: true}>(
				Endpoints.KONVO_AGENTS_THREAD_QUESTION_DISMISS(machineId, threadId, requestId),
				{body: {}},
			);
			return {stopped: response.body?.stopped === true};
		} catch (error) {
			const current = this.dismissing.get(key);
			current?.ids.delete(requestId);
			if (current && current.ids.size === 0) this.dismissing.delete(key);
			runInAction(() => {
				const latest = this.lists.get(machineId);
				if (latest?.threads && before.row) {
					const was = before.row;
					const threads = latest.threads.map((thread) =>
						thread.id === threadId
							? {...thread, needsYou: was.needsYou, ...(was.waitingOn ? {waitingOn: was.waitingOn} : {})}
							: thread,
					);
					this.lists.set(machineId, {...latest, threads});
				}
				if (before.state) this.threads.set(key, before.state);
			});
			throw error;
		} finally {
			// The machine told Konvo before it answered; a read already on its way may still carry it.
			const current = this.dismissing.get(key);
			if (current?.until === Number.POSITIVE_INFINITY) current.until = Date.now() + DISMISS_GRACE_MS;
			void this.loadThread(machineId, threadId);
		}
	}

	// ── Seen marks ───────────────────────────────────────────────────────

	async loadSeen(): Promise<void> {
		try {
			const response = answered(await http.get<AgentsSeen>(Endpoints.KONVO_AGENTS_SEEN, {mode: 'silent'}));
			runInAction(() => {
				// Marks made while this loaded stay.
				const local = this.seen?.threads ?? {};
				const threads = {...response.body.threads};
				for (const [key, at] of Object.entries(local)) if (!threads[key] || threads[key] < at) threads[key] = at;
				this.seen = {since: response.body.since, threads, unread: response.body.unread};
			});
		} catch {
			// Without marks every finished thread reads as seen; the next load tries again.
		}
	}

	/** The person has a finished thread on screen: it no longer shows as not opened, here or elsewhere. */
	markSeen(machineId: string, thread: AgentThreadSummary): void {
		if (!this.isUnseen(machineId, thread)) return;
		const at = finishedAt(thread);
		if (!at || !this.seen) return;
		this.applySeen(machineId, thread.id, at);
		void http
			.post(Endpoints.KONVO_AGENTS_SEEN, {body: {machineId, threadId: thread.id, at}, mode: 'silent'})
			.catch(() => undefined);
	}

	/** The thread is on screen: its push (if one went out) leaves the person's other devices too. */
	markOpened(machineId: string, threadId: string): void {
		void http.post(Endpoints.KONVO_AGENTS_OPENED, {body: {machineId, threadId}, mode: 'silent'}).catch(() => undefined);
		// A chat back from snooze has been seen: it stops saying so.
		const thread = this.summary(machineId, threadId);
		if (thread && this.snoozeState(machineId, thread) === 'back')
			void this.setSnooze(machineId, threadId, null).catch(() => undefined);
	}

	/** Whether the thread can show as not opened: it finished, and does not need the person or fail. */
	canMarkUnseen(machineId: string, thread: AgentThreadSummary): boolean {
		return (
			this.seen != null &&
			!thread.needsYou &&
			thread.status !== 'error' &&
			finishedAt(thread) != null &&
			!this.isUnseen(machineId, thread)
		);
	}

	/** Read or unread, whichever it is not. */
	toggleSeen(machineId: string, thread: AgentThreadSummary): void {
		if (this.isUnseen(machineId, thread)) this.markSeen(machineId, thread);
		else this.markUnseen(machineId, thread);
	}

	/** The other way: a finished thread shows as not opened again until it is opened. */
	markUnseen(machineId: string, thread: AgentThreadSummary): void {
		if (this.isUnseen(machineId, thread)) return;
		const at = finishedAt(thread);
		if (!at || !this.seen) return;
		this.applySeen(machineId, thread.id, at, true);
		void http
			.post(Endpoints.KONVO_AGENTS_SEEN, {body: {machineId, threadId: thread.id, at, unread: true}, mode: 'silent'})
			.catch(() => undefined);
	}

	private applySeen(machineId: string, threadId: string, at: string, unread = false): void {
		if (!this.seen) return;
		const key = seenKey(machineId, threadId);
		const others = (this.seen.unread ?? []).filter((other) => other !== key);
		if (unread) {
			const {[key]: _dropped, ...threads} = this.seen.threads;
			this.seen = {...this.seen, threads, unread: [key, ...others]};
			return;
		}
		const previous = this.seen.threads[key];
		this.seen = {
			...this.seen,
			threads: previous && previous >= at ? this.seen.threads : {...this.seen.threads, [key]: at},
			unread: others,
		};
	}

	// ── Snoozes ──────────────────────────────────────────────────────────

	async loadSnoozes(): Promise<void> {
		try {
			const response = answered(await http.get<AgentsSnoozes>(Endpoints.KONVO_AGENTS_SNOOZES, {mode: 'silent'}));
			runInAction(() => {
				this.snoozes = response.body.snoozes;
			});
		} catch {
			// Nothing shows as snoozed until the next load.
		}
	}

	snoozeOf(machineId: string, threadId: string): AgentSnooze | null {
		const key = seenKey(machineId, threadId);
		return this.snoozes.find((snooze) => snooze.thread === key) ?? null;
	}

	/**
	 * 'asleep' while a snooze holds (it waits in its project's Snoozed group), 'back' once it ended and
	 * the chat wasn't opened since (it says it's back from snooze), else null. Re-reads every minute.
	 */
	snoozeState(machineId: string, thread: AgentThreadSummary): 'asleep' | 'back' | null {
		const snooze = this.snoozeOf(machineId, thread.id);
		if (!snooze) return null;
		return snoozeEnded(snooze, thread, this.minute) ? 'back' : 'asleep';
	}

	isAsleep(machineId: string, thread: AgentThreadSummary): boolean {
		return this.snoozeState(machineId, thread) === 'asleep';
	}

	/** Snoozes a chat until a time or until it needs you, or wakes it (null), here at once and everywhere. */
	async setSnooze(
		machineId: string,
		threadId: string,
		snooze: {until: Date} | {untilNeedsYou: true} | null,
	): Promise<void> {
		const key = seenKey(machineId, threadId);
		const before = this.snoozes;
		const others = before.filter((other) => other.thread !== key);
		this.snoozes = snooze
			? [
					{
						thread: key,
						...('until' in snooze ? {until: snooze.until.toISOString()} : {untilNeedsYou: true as const}),
						at: new Date().toISOString(),
					},
					...others,
				]
			: others;
		try {
			const response = await http.post<AgentsSnoozes>(Endpoints.KONVO_AGENTS_SNOOZES, {
				body: {
					machineId,
					threadId,
					...(snooze && 'until' in snooze ? {until: snooze.until.toISOString()} : {}),
					...(snooze && 'untilNeedsYou' in snooze ? {untilNeedsYou: true} : {}),
				},
			});
			runInAction(() => {
				this.snoozes = response.body.snoozes;
			});
		} catch (error) {
			runInAction(() => {
				this.snoozes = before;
			});
			throw error;
		}
	}

	// ── Pins ─────────────────────────────────────────────────────────────

	async loadPins(): Promise<void> {
		try {
			const response = answered(await http.get<AgentsPins>(Endpoints.KONVO_AGENTS_PINS, {mode: 'silent'}));
			runInAction(() => {
				this.pins = response.body.threads;
			});
		} catch {
			// Nothing pinned shows until the next load.
		}
	}

	/** Each pinned thread's place among the pins: 0 for the latest. */
	get pinRanks(): Map<string, number> {
		return new Map(this.pins.map((key, index) => [key, index]));
	}

	isPinned(machineId: string, threadId: string): boolean {
		return this.pinRanks.has(seenKey(machineId, threadId));
	}

	/** The machine's pinned threads: thread id -> its place among the pins (the list orders by it). */
	pinsOf(machineId: string): Map<string, number> {
		const prefix = `${machineId}/`;
		const ranks = new Map<string, number>();
		for (const [key, rank] of this.pinRanks) if (key.startsWith(prefix)) ranks.set(key.slice(prefix.length), rank);
		return ranks;
	}

	/**
	 * Settled by the agent tool and not pinned: the thread sits under Settled. A pinned thread stays
	 * in the list where it was pinned.
	 */
	isSettledAway(machineId: string, thread: AgentThreadSummary): boolean {
		return isSettled(thread) && !this.isPinned(machineId, thread.id);
	}

	/** Pins the thread on top of its group (or unpins it), here at once and on the person's other devices. */
	async setPinned(machineId: string, threadId: string, pinned: boolean): Promise<void> {
		const key = seenKey(machineId, threadId);
		const without = (pins: Array<string>) => pins.filter((other) => other !== key);
		this.pins = pinned ? [key, ...without(this.pins)] : without(this.pins);
		try {
			await http.post(Endpoints.KONVO_AGENTS_PINS, {body: {machineId, threadId, pinned}});
		} catch (error) {
			runInAction(() => {
				this.pins = pinned ? without(this.pins) : [key, ...without(this.pins)];
			});
			throw error;
		}
	}

	// ── Project icons ────────────────────────────────────────────────────

	async loadProjectIcons(): Promise<void> {
		try {
			const response = answered(
				await http.get<AgentsProjectIcons>(Endpoints.KONVO_AGENTS_PROJECT_ICONS, {mode: 'silent'}),
			);
			runInAction(() => {
				this.projectIcons = response.body.icons;
			});
		} catch {
			// Projects keep the folder until the next load.
		}
	}

	/** The icon the person set on this project, or null: its favicon, else the folder. */
	projectIconOf(machineId: string, projectId: string): AgentProjectIcon | null {
		return this.projectIcons[seenKey(machineId, projectId)] ?? null;
	}

	/** The favicon the machine found in the project's folder (bridge 0.63.0+), or null. */
	projectFaviconOf(machineId: string, projectId: string): string | null {
		return this.machine(machineId)?.projects.find((p) => p.id === projectId)?.favicon ?? null;
	}

	/** Sets a project's icon (null: back to the folder), here at once and on the person's other devices. */
	async setProjectIcon(machineId: string, projectId: string, icon: AgentProjectIcon | null): Promise<void> {
		const key = seenKey(machineId, projectId);
		const before = this.projectIcons;
		const {[key]: _old, ...others} = before;
		this.projectIcons = icon ? {[key]: icon, ...others} : others;
		try {
			const response = await http.post<AgentsProjectIcons>(Endpoints.KONVO_AGENTS_PROJECT_ICONS, {
				body: {machineId, projectId, icon},
			});
			runInAction(() => {
				this.projectIcons = response.body.icons;
			});
		} catch (error) {
			runInAction(() => {
				this.projectIcons = before;
			});
			throw error;
		}
	}

	/** What the sidebar badge needs: the overview, seen marks and each online machine's threads. */
	async loadForBadge(): Promise<void> {
		if (!this.loaded) await this.loadOverview();
		if (!this.enabled) return;
		if (!this.seen) await this.loadSeen();
		if (AgentPulls.asks === null) void AgentPulls.loadAsks();
		this.ensureLists();
	}

	/** Thread lists for every online machine, for the sidebar badge (once; live updates keep them fresh). */
	ensureLists(): void {
		for (const machine of this.machines) {
			if (machine.online && !this.lists.has(machine.id)) void this.loadThreads(machine.id);
		}
	}

	/** A machine as the API just returned it (its settings changed): the list shows it at once. */
	applyMachine(machine: AgentMachineView): void {
		this.machines = this.machines.map((entry) => (entry.id === machine.id ? machine : entry));
	}

	async rename(machineId: string, name: string): Promise<void> {
		const response = await http.patch<Partial<AgentMachineView>>(Endpoints.KONVO_AGENTS_MACHINE(machineId), {
			body: {name},
		});
		runInAction(() => {
			const next = response.body && typeof response.body.name === 'string' ? response.body.name : name;
			this.machines = this.machines.map((machine) => (machine.id === machineId ? {...machine, name: next} : machine));
		});
	}

	async disconnect(machineId: string): Promise<void> {
		await http.post(Endpoints.KONVO_AGENTS_MACHINE_DISCONNECT(machineId), {body: {}});
		runInAction(() => {
			this.machines = this.machines.filter((machine) => machine.id !== machineId);
			this.lists.delete(machineId);
			for (const key of [...this.threads.keys()]) if (key.startsWith(`${machineId}\u0000`)) this.threads.delete(key);
		});
	}

	async createPairing(): Promise<AgentPairing> {
		const response = await http.post<AgentPairing>(Endpoints.KONVO_AGENTS_PAIRING, {body: {}});
		return response.body;
	}

	// ── Notification settings ────────────────────────────────────────────

	async loadPrefs(): Promise<void> {
		try {
			const response = answered(await http.get<AgentsPrefs>(Endpoints.KONVO_AGENTS_PREFS, {mode: 'silent'}));
			runInAction(() => {
				this.prefs = response.body;
				this.prefsError = null;
			});
		} catch (error) {
			runInAction(() => {
				this.prefsError = error;
			});
		}
	}

	/** What Enter does with a prompt while the agent works; ⌘/Ctrl+Enter does the other. */
	get followUp(): 'steer' | 'queue' {
		return this.prefs?.followUp ?? 'steer';
	}

	/** Saves at once; a failure puts the switches back and rejects. */
	async savePrefs(patch: Partial<AgentsPrefs>): Promise<void> {
		const before = this.prefs;
		if (!before) return;
		const next = {...before, ...patch};
		this.prefs = next;
		try {
			const response = await http.put<AgentsPrefs>(Endpoints.KONVO_AGENTS_PREFS, {body: next});
			runInAction(() => {
				if (response.body && typeof response.body === 'object' && 'scope' in response.body) this.prefs = response.body;
			});
		} catch (error) {
			runInAction(() => {
				this.prefs = before;
			});
			throw error;
		}
	}

	// ── Realtime ─────────────────────────────────────────────────────────

	applyEvent(event: AgentsUpdateEvent): void {
		if (event.kind === 'machines') {
			void this.loadOverview().then(() => {
				// A machine that just came online, or whose coding app answers again, shows its chats without
				// waiting for a list event (the bridge only sends one when the list changed).
				if (!this.listsWanted) return;
				for (const machine of this.machines) {
					const list = this.lists.get(machine.id);
					if (machine.online && (!list || (list.error && !list.loading && machine.tools.length)))
						void this.loadThreads(machine.id);
				}
			});
			return;
		}
		if (event.kind === 'pins') {
			this.pins = event.threads;
			return;
		}
		if (event.kind === 'snoozes') {
			this.snoozes = event.snoozes;
			return;
		}
		if (event.kind === 'project_icons') {
			this.projectIcons = event.icons;
			return;
		}
		if (event.kind === 'seen') {
			this.applySeen(event.machineId, event.threadId, event.at, event.unread === true);
			return;
		}
		if (event.kind === 'queued') {
			void this.loadQueued();
			return;
		}
		if (event.kind === 'merge_asks') {
			void AgentPulls.loadAsks();
			return;
		}
		if (event.kind === 'pulls') {
			AgentPullInbox.refresh(event.changed);
			return;
		}
		if (event.kind === 'runs') {
			AgentRuns.refresh();
			return;
		}
		if (event.kind === 'asks') {
			AgentAsks.applyUpdate(event.machineId, event.threadId);
			return;
		}
		// Team threads: sharing changed on a thread, or what is shared with the person.
		if (event.kind === 'share' || event.kind === 'shared') {
			AgentShares.applyEvent(event);
			return;
		}
		if (event.kind === 'threads') {
			const current = this.lists.get(event.machineId);
			this.lists.set(event.machineId, {
				threads: this.withArchiving(event.machineId, this.withSettling(event.machineId, event.threads)),
				projects: current?.projects ?? [],
				loading: false,
				error: null,
				loadedAt: Date.now(),
			});
			this.forgetStarted(event.machineId);
			// An open thread whose summary moved on gets re-read (its watch may have lapsed).
			for (const summary of event.threads) {
				const state = this.threads.get(threadKey(event.machineId, summary.id));
				if (state?.detail && !state.loading && summaryDiffers(state.detail, summary)) {
					void this.loadThread(event.machineId, summary.id);
				}
			}
			return;
		}
		// Newer than any read already asked for: one of those landing after it is not shown.
		this.appliedThreadRead.set(threadKey(event.machineId, event.thread.id), ++this.threadReads);
		this.putDetail(event.machineId, event.thread);
	}

	setRetryAt(at: number | null): void {
		this.retryAt = at;
	}

	setListsWanted(wanted: boolean): void {
		this.listsWanted = wanted;
	}

	// ── Small UI hand-offs ───────────────────────────────────────────────

	/** Turns a list row's title into a field (the row menu's Rename). */
	startListRename(machineId: string, threadId: string): void {
		this.listRename = {machineId, threadId};
	}

	stopListRename(): void {
		this.listRename = null;
	}

	isListRenaming(machineId: string, threadId: string): boolean {
		return this.listRename?.machineId === machineId && this.listRename.threadId === threadId;
	}

	pickListMachine(machineId: string | null): void {
		this.listMachineId = machineId;
		if (this.settledMachineId && this.settledMachineId !== machineId) this.showSettled(null);
		if (this.archivedMachineId && this.archivedMachineId !== machineId) this.showArchived(null);
	}

	/**
	 * The desktop list follows the chat that opens: a newly opened chat shows its machine's list, and a
	 * machine picked while that chat stays open still switches the list (it used to snap back to the open chat's machine).
	 */
	followOpenThread(machineId: string | null, threadId: string | null): void {
		const key = followKey(machineId, threadId);
		if (key === this.listFollowed) return;
		this.listFollowed = key;
		if (machineId && machineId !== this.listMachineId) this.pickListMachine(machineId);
	}

	/** The machine the desktop list shows with this chat open: the chat's own until followOpenThread has run. */
	listMachineFor(machineId: string | null, threadId: string | null): string | null {
		const key = followKey(machineId, threadId);
		return key !== null && key !== this.listFollowed ? machineId : this.listMachineId;
	}

	/** Where the next new chat starts (Fix in a new chat: a worktree off the pull request's branch); taken once. */
	newChatStart: {branch: string; worktree: boolean} | null = null;

	setNewChatStart(start: {branch: string; worktree: boolean} | null): void {
		this.newChatStart = start;
	}

	/** The start the new chat page takes, once. */
	takeNewChatStart(): {branch: string; worktree: boolean} | null {
		const start = this.newChatStart;
		this.newChatStart = null;
		return start;
	}

	/** For a login whose plan is used up: the machine's other login of that agent with room, if any. */
	otherLogin(
		machineId: string,
		from: {instance: string; model?: string | null; effort?: string | null},
	): OtherLogin | null {
		return otherLoginWithRoom(this.plans, this.machine(machineId), from);
	}

	/**
	 * Readies the new chat page to carry on a chat that stopped on its plan, on another login: the
	 * old chat's asks and last reply as context, that login's model and a first prompt to edit.
	 */
	prepareContinueElsewhere(next: {context: AgentChatContext; model: AgentModelChoice; prompt: string}): void {
		this.setNewChatContext(next.context);
		this.newChatModel = next.model;
		AgentDrafts.set(NEW_CHAT_DRAFT, next.prompt);
	}

	clearNewChatModel(): void {
		this.newChatModel = null;
	}

	setNewChatContext(context: AgentChatContext | null, guess: string | null = null): void {
		this.newChatContext = context;
		this.newChatGuess = context ? guess : null;
	}

	showSettled(machineId: string | null): void {
		this.settledMachineId = machineId;
		this.settledProject = null;
		if (machineId) this.archivedMachineId = null;
	}

	/** Swaps the machine's live list for its archived chats (null: back to the live list). */
	showArchived(machineId: string | null): void {
		this.archivedMachineId = machineId;
		if (machineId) {
			this.showSettled(null);
			void this.loadArchived(machineId);
		}
	}

	pickSettledProject(projectId: string | null): void {
		this.settledProject = projectId;
	}

	notifyWhenBack(machineId: string): void {
		this.waitingFor.add(machineId);
	}

	requestManager(): void {
		this.managerRequested = true;
	}

	/** Read once by Settings → Connections when it opens. */
	consumeManagerRequest(): boolean {
		const requested = this.managerRequested;
		this.managerRequested = false;
		return requested;
	}

	reset(): void {
		this.setupOpen.clear();
		this.enabled = null;
		this.machines = [];
		this.loaded = false;
		this.loading = false;
		this.error = null;
		this.lists.clear();
		this.threads.clear();
		this.prefs = null;
		this.prefsError = null;
		this.seen = null;
		this.pins = [];
		this.snoozes = [];
		this.projectIcons = {};
		this.queued = null;
		this.waitingFor.clear();
		this.managerRequested = false;
		this.overviewRequest = null;
		this.outbox.clear();
		this.queueActions.clear();
		this.editingSends.clear();
		for (const id of [...commitWaits.keys()]) this.stopCommitWait(id);
		this.pendingStarts.clear();
		this.settling.clear();
		this.renaming.clear();
		this.archiving.clear();
		this.archived.clear();
		this.archivedMachineId = null;
		this.dismissing.clear();
		this.listRename = null;
		this.sendChains.clear();
	}
}

export default new AgentsStore();
